import React, { useEffect, useState } from "react";
import { api, ApiError, type Property, type Tenancy, type PropertyUnit } from "../../api";
import { useAuth } from "../../AuthContext";
import { useToast } from "../../ToastContext";
import { PropertyPhotos } from "./PropertyPhotos";
import { PhotoStager } from "./PhotoStager";
import { EndTenancyForm } from "./EndTenancyForm";
import { EditPropertyForm } from "./EditPropertyForm";
import { FurnishingItemsEditor, type CustomItemDraft } from "./CustomFurnishingItems";
import { FURNISHING_ITEM_KEYS } from "../../furnishingItems";
import { LocationCaptureField, type PropertyLocation } from "../shared/LocationCapture";
import { RentalAgreement } from "../shared/RentalAgreement";
import { PropertyAnalytics } from "./PropertyAnalytics";
import { BoostListing } from "./BoostListing";
import { RentReceipt, type ReceiptEntry } from "../shared/RentReceipt";
import { ContactPickerButton } from "../shared/ContactPickerButton";
import { AadhaarPhotoFields } from "../shared/AadhaarPhotoFields";
import { Select } from "../shared/Select";
import { INDIAN_STATES } from "../../indianStates";
import { useLanguage } from "../../i18n";
import { fmtMonth } from "../../format";

function fmtINR(n: number | null | undefined) {
  if (n == null) return "—";
  return "₹" + Number(n).toLocaleString("en-IN");
}

const HOUSE_ICON = (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.1" strokeLinecap="round" strokeLinejoin="round">
    <path d="m3 9 9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2Z" />
    <path d="M9 22V12h6v10" />
  </svg>
);
const PG_ICON = (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.1" strokeLinecap="round" strokeLinejoin="round">
    <path d="M2 20h20M4 20V10l8-6 8 6v10M9 20v-6h6v6" />
  </svg>
);
const COMMERCIAL_ICON = (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.1" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 21h18M4 21V8l8-5 8 5v13M9 21v-5h6v5M9 12h.01M15 12h.01M9 8h.01M15 8h.01" />
  </svg>
);

// Property has no photo thumb of its own here (unlike the Home dashboard's
// PropThumb) — a category glyph keeps the banner header visually consistent
// with Home's cards without needing photos loaded up front.
function PropertyIcon({ category, type }: { category: Property["category"]; type: string | null }) {
  const icon = category === "pg" ? PG_ICON : COMMERCIAL_TYPES.includes(type ?? "") ? COMMERCIAL_ICON : HOUSE_ICON;
  return <div className="prop-thumb">{icon}</div>;
}

// Shown right after adding any tenant with a phone on file — lets the owner
// notify them on WhatsApp so they open the app and complete onboarding (see
// TenantOnboarding.tsx). Copy varies slightly depending on whether the
// phone already matched a registered account: an unlinked tenant needs to
// install/register first, a linked one just needs to open the app. The link
// points to the Play Store listing.
function InstallAppPrompt({
  tenantName,
  tenantPhone,
  propertyLabel,
  hasAccount,
  onDismiss,
}: {
  tenantName: string;
  tenantPhone: string;
  propertyLabel: string;
  hasAccount: boolean;
  onDismiss: () => void;
}) {
  const { t } = useLanguage();
  const { user } = useAuth();
  const msg = t(hasAccount ? "ownerListings.notifyTenantWhatsappMessage" : "ownerListings.installAppWhatsappMessage", {
    owner: user?.name ?? "",
    property: propertyLabel,
    link: "https://play.google.com/store/apps/details?id=in.toletbook.app",
  });
  return (
    <div className="card" style={{ marginBottom: 10, background: "var(--brand-100)" }}>
      <b style={{ fontSize: 12.5 }}>
        {t(hasAccount ? "ownerListings.notifyTenantPromptTitle" : "ownerListings.installAppPromptTitle", { name: tenantName })}
      </b>
      <div className="sub" style={{ marginTop: 2, marginBottom: 10 }}>
        {t(hasAccount ? "ownerListings.notifyTenantPromptBody" : "ownerListings.installAppPromptBody")}
      </div>
      <div style={{ display: "flex", gap: 8 }}>
        <a
          className="btn-solid btn-sm"
          style={{ flex: 1, textAlign: "center", textDecoration: "none" }}
          href={`https://wa.me/91${tenantPhone}?text=${encodeURIComponent(msg)}`}
          target="_blank"
          rel="noopener noreferrer"
          onClick={onDismiss}
        >
          {t("ownerListings.shareOnWhatsappBtn")}
        </a>
        <button className="btn-outline btn-sm" style={{ flex: 1 }} onClick={onDismiss}>
          {t("common.cancel")}
        </button>
      </div>
    </div>
  );
}

// Shown when a Basic owner tries to enable a second TO-LET slot on the same
// property (or a property with units already using its one free slot) — the
// backend enforces the actual cap (see assertCanEnableToLet in
// properties.ts); this is just the upsell surfaced from that 402.
function ToLetLimitSheet({
  onClose,
  onNavigate,
}: {
  onClose: () => void;
  onNavigate?: (view: "plans" | "refer") => void;
}) {
  const { t } = useLanguage();
  return (
    <>
      <div className="filter-backdrop" onClick={onClose} />
      <div className="filter-sheet">
        <div className="filter-sheet-handle" />
        <div className="filter-sheet-head">
          <div className="filter-sheet-title">{t("ownerListings.toLetLimitTitle")}</div>
          <div className="filter-sheet-close" onClick={onClose}>✕</div>
        </div>
        <p className="sub" style={{ lineHeight: 1.6, margin: "4px 0 18px" }}>
          {t("ownerListings.toLetLimitBody")}
        </p>
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          <button
            className="btn-solid"
            onClick={() => {
              onClose();
              onNavigate?.("plans");
            }}
          >
            {t("ownerListings.toLetLimitUpgradeBtn")}
          </button>
          <button
            className="btn-outline"
            onClick={() => {
              onClose();
              onNavigate?.("refer");
            }}
          >
            {t("ownerListings.toLetLimitReferBtn")}
          </button>
        </div>
      </div>
    </>
  );
}

export function OwnerListings({
  focusPropertyId,
  onNavigate,
}: { focusPropertyId?: string | null; onNavigate?: (view: "plans" | "refer") => void } = {}) {
  const [properties, setProperties] = useState<Property[] | null>(null);
  // "__add__" is a sentinel focusPropertyId — set by Home's Add Property
  // buttons so this screen opens straight into the add wizard instead of
  // the plain list (no real property ever has this id).
  const [adding, setAdding] = useState(() => focusPropertyId === "__add__");
  const [expanded, setExpanded] = useState<string | null>(() => (focusPropertyId && focusPropertyId !== "__add__" ? focusPropertyId : null));
  // null = still loading (or unlimited — Infinity doesn't survive JSON, it
  // arrives as null) — either way, don't block the Add button on a cap we
  // don't actually know yet.
  const [listingCap, setListingCap] = useState<number | null>(null);
  const toast = useToast();
  const { t } = useLanguage();

  async function load() {
    const { properties } = await api.get("/api/properties/mine");
    setProperties(properties);
  }

  useEffect(() => {
    load();
    api
      .get("/api/subscription")
      .then((s) => setListingCap(typeof s?.limits?.listingCap === "number" ? s.limits.listingCap : null))
      .catch(() => {});
  }, []);

  // Checking the cap here — before the Add form even opens — avoids an
  // owner filling out the whole form only to get blocked at submit. The
  // server still enforces this for real (AddPropertyForm's own error
  // banner is the fallback if the cap changed between page load and click).
  function handleAddClick() {
    if (listingCap != null && (properties?.length ?? 0) >= listingCap) {
      toast(t("ownerListings.upgradeToastMsg"));
      onNavigate?.("plans");
      return;
    }
    setAdding(true);
  }

  if (adding) {
    return (
      <AddPropertyForm
        onDone={async () => {
          setAdding(false);
          await load();
          toast(t("ownerListings.propertyAdded"));
        }}
        onCancel={() => setAdding(false)}
        onNavigate={onNavigate}
      />
    );
  }

  if (!properties) return <div className="spinner" />;

  return (
    <>
      <div className="section-title">
        {t("ownerListings.heading")}
      </div>
      {properties.length === 0 && (
        <div className="empty-add-cta">
          <button onClick={handleAddClick} aria-label={t("ownerListings.addLink")}>+</button>
          <span>{t("ownerListings.emptyState")}</span>
        </div>
      )}
      {properties.map((p) => (
        <PropertyCard
          key={p.id}
          property={p}
          expanded={expanded === p.id}
          onToggle={() => setExpanded(expanded === p.id ? null : p.id)}
          onChanged={load}
          autoAssign={focusPropertyId === p.id}
          onNavigate={onNavigate}
        />
      ))}
      {properties.length > 0 && (
        <div className="empty-add-cta" style={{ padding: "24px 12px 8px" }}>
          <button onClick={handleAddClick} aria-label={t("ownerListings.addPropertyAria")}>+</button>
          <span>{t("ownerListings.addPropertyAria")}</span>
        </div>
      )}
    </>
  );
}

function PropertyCard({
  property,
  expanded,
  onToggle,
  onChanged,
  autoAssign,
  onNavigate,
}: {
  property: Property;
  expanded: boolean;
  onToggle: () => void;
  onChanged: () => void;
  autoAssign?: boolean;
  onNavigate?: (view: "plans" | "refer") => void;
}) {
  const [editing, setEditing] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [showLimitSheet, setShowLimitSheet] = useState(false);
  const toast = useToast();
  const { t } = useLanguage();

  const isVacant =
    property.category !== "pg" ? property.status === "vacant" : (property.occupancySummary?.vacantBeds ?? 0) > 0;
  const hasUnits = !!property.units?.length;

  async function deleteProperty() {
    if (!confirm(t("ownerListings.confirmDelete", { label: property.label }))) return;
    await api.del(`/api/properties/${property.id}`);
    toast(t("ownerListings.propertyDeleted"));
    onChanged();
  }

  async function toggleToLet() {
    try {
      const { property: updated } = await api.patch(`/api/properties/${property.id}`, { toLet: !property.toLet });
      toast(updated.toLet ? t("ownerListings.listedOnMarketplace") : t("ownerListings.removedFromMarketplace"));
      onChanged();
    } catch (err) {
      if (err instanceof ApiError && err.status === 402) {
        setShowLimitSheet(true);
      } else {
        toast(err instanceof ApiError ? err.message : t("ownerListings.couldNotUpdateListing"));
      }
    }
  }

  function openEdit() {
    setMenuOpen(false);
    if (!expanded) onToggle();
    setEditing(true);
  }

  return (
    <div style={{ position: "relative", marginBottom: 10 }}>
    <div className="prop-card">
      <div className="prop-top" onClick={onToggle} style={{ cursor: "pointer" }}>
        <div className="prop-top-main">
          <PropertyIcon category={property.category} type={property.type} />
          <div>
            <div className="prop-addr">{property.label}</div>
            <div className="prop-locality">{property.locality}</div>
          </div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          {property.category !== "pg" && !property.units?.length ? (
            <>
              {property.boosted && <span className="badge" style={{ background: "var(--cash)", color: "var(--cash-ink)" }}>⚡</span>}
              <span className={`badge ${property.status}`}>
                {property.status === "occupied" ? t("ownerListings.occupiedBadge") : t("ownerListings.vacantBadge")}
              </span>
            </>
          ) : (
            <span className="badge vacant">
              {t("ownerListings.vacantBedsFraction", { vacant: property.occupancySummary?.vacantBeds ?? 0, total: property.occupancySummary?.totalBeds ?? 0 })}
            </span>
          )}
          <button
            className="icon-btn"
            style={{ width: 30, height: 30, flexShrink: 0 }}
            onClick={(e) => {
              e.stopPropagation();
              setMenuOpen((v) => !v);
            }}
            aria-label={t("ownerListings.moreOptionsAria")}
          >
            ⋮
          </button>
        </div>
      </div>

      {expanded && (
        <div className="prop-body">
          {editing ? (
            <EditPropertyForm
              property={property}
              onCancel={() => setEditing(false)}
              onDone={() => {
                setEditing(false);
                onChanged();
              }}
            />
          ) : (
            <>
              <PropertyPhotos property={property} onChanged={onChanged} />
              {property.category !== "pg" && !hasUnits && (
                <div className="sub" style={{ marginBottom: 10 }}>
                  {property.type} &middot; {t("ownerListings.expectedRentLabel")} {fmtINR(property.expectedRent)} &middot; {t("ownerListings.depositLabel")} {fmtINR(property.expectedDeposit)}
                </div>
              )}
              {isVacant && !hasUnits && (
                <div className="vacant-row" style={{ marginBottom: 12 }}>
                  <div className="vacant-info">
                    <div className="vacant-label">
                      {property.category !== "pg" ? t("ownerListings.listOnToLet") : t("ownerListings.listVacantBedsToLet")}
                    </div>
                    <div className="vacant-sub">
                      {property.toLet ? (<><span className="live-dot" /> {t("ownerListings.liveVisible")}</>) : t("ownerListings.notListedYet")}
                    </div>
                  </div>
                  <button
                    className={`toggle${property.toLet ? " on" : ""}`}
                    onClick={toggleToLet}
                    aria-label={t("ownerListings.toggleToLetAria")}
                  >
                    <i />
                  </button>
                </div>
              )}
              {property.category !== "pg" ? (
                property.units ? (
                  <MultiUnitDetail property={property} onChanged={onChanged} onNavigate={onNavigate} />
                ) : (
                  <IndependentDetail property={property} onChanged={onChanged} autoAssign={autoAssign} onNavigate={onNavigate} />
                )
              ) : (
                <PgDetail property={property} onChanged={onChanged} />
              )}
              {showLimitSheet && <ToLetLimitSheet onClose={() => setShowLimitSheet(false)} onNavigate={onNavigate} />}
            </>
          )}
        </div>
      )}
    </div>

    {menuOpen && (
      <>
        <div style={{ position: "fixed", inset: 0, zIndex: 20 }} onClick={() => setMenuOpen(false)} />
        <div
          className="menu-group"
          style={{ position: "absolute", top: 78, right: 15, zIndex: 21, minWidth: 180, marginTop: 0, boxShadow: "0 16px 32px -10px rgba(0,0,0,.45)" }}
        >
          <div className="menu-item" onClick={openEdit}>
            {t("ownerListings.editDetails")}
          </div>
          <div
            className="menu-item danger"
            onClick={() => {
              setMenuOpen(false);
              deleteProperty();
            }}
          >
            {t("ownerListings.deleteProperty")}
          </div>
        </div>
      </>
    )}
    </div>
  );
}

function IndependentDetail({
  property,
  onChanged,
  autoAssign,
  onNavigate,
}: {
  property: Property;
  onChanged: () => void;
  autoAssign?: boolean;
  onNavigate?: (view: "plans" | "refer") => void;
}) {
  const [assigning, setAssigning] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [editingTenant, setEditingTenant] = useState(false);
  const [showAgreement, setShowAgreement] = useState(false);
  const [showAnalytics, setShowAnalytics] = useState(false);
  // Collapses the share prompt for this viewing only — it comes back next
  // time the property is opened, since it's driven by onboarding status
  // (server data), not one-shot local state.
  const [promptDismissed, setPromptDismissed] = useState(false);
  const toast = useToast();
  const { user } = useAuth();
  const { t } = useLanguage();

  // Deep-linked here from the "Add Tenant" shortcut on the Home dashboard —
  // skip straight to the form instead of making them tap "+ Add tenant" again.
  useEffect(() => {
    if (autoAssign && !property.tenancy) setAssigning(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (showAnalytics) {
    return (
      <div className="doc-overlay">
        <PropertyAnalytics
          propertyId={property.id}
          propertyLabel={property.label}
          onBack={() => setShowAnalytics(false)}
          onNavigate={onNavigate}
        />
      </div>
    );
  }

  if (showAgreement && property.tenancy) {
    return (
      <div className="doc-overlay">
        <RentalAgreement
          tenancyId={property.tenancy.id}
          tenantName={property.tenancy.name}
          tenantPhone={property.tenancy.phone}
          ownerName={user?.name}
          ownerPhone={user?.phone}
          propertyLabel={property.label}
          propertyLocality={property.locality}
          propertyType={property.type}
          rent={property.tenancy.rent}
          deposit={property.tenancy.deposit}
          startDate={property.tenancy.startDate}
          onBack={() => setShowAgreement(false)}
        />
      </div>
    );
  }

  return (
    <div>
      <BoostListing property={property} onChanged={onChanged} />
      {property.tenancy && property.tenancy.onboardingStatus === "pending" && property.tenancy.phone && !promptDismissed && (
        <InstallAppPrompt
          tenantName={property.tenancy.name}
          tenantPhone={property.tenancy.phone}
          propertyLabel={property.label}
          hasAccount={!!property.tenancy.tenantId}
          onDismiss={() => setPromptDismissed(true)}
        />
      )}
      {property.tenancy && (
        <button className="btn-outline btn-sm" style={{ width: "100%", marginBottom: 10 }} onClick={() => setShowAgreement(true)}>
          {t("ownerListings.rentalAgreementButton")} <span className="mi-right">›</span>
        </button>
      )}
      <button className="btn-outline btn-sm" style={{ width: "100%", marginBottom: 10 }} onClick={() => setShowAnalytics(true)}>
        {t("ownerListings.analyticsButton")} <span className="mi-right">›</span>
      </button>
      {property.tenancy ? (
        <div className="card" style={{ background: "var(--surface-alt)" }}>
          <div className="card-row">
            <div>
              <b>{property.tenancy.name}</b>
              <div className="sub">{property.tenancy.phone || t("ownerListings.noPhoneOnFile")}</div>
            </div>
            <span className="badge occupied">{fmtINR(property.tenancy.rent)}{t("ownerListings.perMonth")}</span>
          </div>
          {editingTenant ? (
            <EditTenantForm
              tenancy={property.tenancy}
              onCancel={() => setEditingTenant(false)}
              onSubmit={async (form) => {
                await api.patch(`/api/tenancies/${property.tenancy!.id}`, form);
                setEditingTenant(false);
                toast(t("ownerListings.tenantUpdated"));
                onChanged();
              }}
            />
          ) : removing ? (
            <EndTenancyForm
              tenancy={property.tenancy}
              onCancel={() => setRemoving(false)}
              onDone={() => {
                setRemoving(false);
                onChanged();
              }}
            />
          ) : (
            <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
              <button className="btn-outline btn-sm" style={{ flex: 1 }} onClick={() => setEditingTenant(true)}>
                {t("ownerListings.editTenantButton")}
              </button>
              <button className="btn-outline danger btn-sm" style={{ flex: 1 }} onClick={() => setRemoving(true)}>
                {t("ownerListings.endTenancyButton")}
              </button>
            </div>
          )}
        </div>
      ) : assigning ? (
        <AssignTenantForm
          defaultRent={property.expectedRent}
          defaultDeposit={property.expectedDeposit}
          onSubmit={async (form) => {
            await api.post(`/api/properties/${property.id}/tenant`, form);
            setAssigning(false);
            toast(t("ownerListings.tenantAdded"));
            onChanged();
          }}
          onCancel={() => setAssigning(false)}
        />
      ) : (
        <button className="btn-outline btn-sm" onClick={() => setAssigning(true)}>
          {t("ownerListings.addTenantLink")}
        </button>
      )}
    </div>
  );
}

// An independent property that's a compound of several standalone houses —
// each is its own rentable unit with its own tenant, rather than the one
// property-level tenancy IndependentDetail handles.
function MultiUnitDetail({
  property,
  onChanged,
  onNavigate,
}: {
  property: Property;
  onChanged: () => void;
  onNavigate?: (view: "plans" | "refer") => void;
}) {
  const [addingUnit, setAddingUnit] = useState(false);
  const { t } = useLanguage();
  const units = property.units ?? [];
  const listedCount = units.filter((u) => u.toLet).length;

  return (
    <div>
      <div className="section-title" style={{ margin: "0 0 2px" }}>
        {t("addPropertyForm.housesHeading")}
      </div>
      {units.length > 0 && (
        <div className="sub" style={{ marginBottom: 10 }}>
          {t("ownerListings.housesOnToLetSummary", { listed: listedCount, total: units.length })}
        </div>
      )}
      {units.map((u) => (
        <UnitRow
          key={u.id}
          unit={u}
          propertyLabel={property.label}
          category={property.category}
          onChanged={onChanged}
          onNavigate={onNavigate}
        />
      ))}
      {addingUnit ? (
        <UnitForm
          category={property.category}
          onCancel={() => setAddingUnit(false)}
          onSubmit={async (form) => {
            await api.post(`/api/properties/${property.id}/units`, form);
            setAddingUnit(false);
            onChanged();
          }}
        />
      ) : (
        <button className="btn-outline btn-sm" style={{ width: "100%" }} onClick={() => setAddingUnit(true)}>
          {t("addPropertyForm.addHouseLink")}
        </button>
      )}
    </div>
  );
}

function UnitForm({
  unit,
  category,
  onSubmit,
  onCancel,
}: {
  unit?: PropertyUnit;
  category?: "independent" | "pg" | "commercial";
  onSubmit: (form: {
    label: string;
    type: string;
    expectedRent: number;
    expectedDeposit: number;
    maintenance: number;
    furnishingStatus: string | null;
    communityType: string | null;
    floorNumber: string | null;
    furnishingItems: Record<string, number>;
  }) => Promise<void>;
  onCancel: () => void;
}) {
  const [label, setLabel] = useState(unit?.label ?? "");
  const [type, setType] = useState(unit?.type ?? "2BHK");
  const [expectedRent, setExpectedRent] = useState(String(unit?.expectedRent ?? ""));
  const [expectedDeposit, setExpectedDeposit] = useState(String(unit?.expectedDeposit ?? ""));
  const [maintenance, setMaintenance] = useState(String(unit?.maintenance ?? ""));
  const [furnishingStatus, setFurnishingStatus] = useState<string | null>(unit?.furnishingStatus ?? null);
  const [communityType, setCommunityType] = useState<string | null>(unit?.communityType ?? null);
  const [floorNumber, setFloorNumber] = useState(unit?.floorNumber ?? "");
  const [furnishingItems, setFurnishingItems] = useState<Record<string, string>>(() => {
    const init: Record<string, string> = {};
    for (const key of FURNISHING_ITEM_KEYS) init[key] = String(unit?.furnishingItems?.[key] ?? "");
    return init;
  });
  const [customItems, setCustomItems] = useState<CustomItemDraft[]>(() =>
    Object.entries(unit?.furnishingItems ?? {})
      .filter(([key]) => !(FURNISHING_ITEM_KEYS as readonly string[]).includes(key))
      .map(([name, qty]) => ({ name, qty: String(qty) }))
  );
  // Starts with every preset "not added" (list opens empty) except ones
  // that already carry a saved quantity — see FurnishingItemsEditor.
  const [removedPresets, setRemovedPresets] = useState<string[]>(() =>
    FURNISHING_ITEM_KEYS.filter((key) => !unit?.furnishingItems?.[key])
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const { t } = useLanguage();

  async function submit() {
    if (!label.trim()) return setError(t("addPropertyForm.errNameRequired"));
    setBusy(true);
    setError("");
    try {
      await onSubmit({
        label,
        type,
        expectedRent: Number(expectedRent) || 0,
        expectedDeposit: Number(expectedDeposit) || 0,
        maintenance: Number(maintenance) || 0,
        furnishingStatus,
        communityType: category === "commercial" ? null : communityType,
        floorNumber:
          category === "commercial"
            ? floorNumber.trim() || null
            : FLOOR_COMMUNITY_TYPES.includes(communityType ?? "")
            ? floorNumber.trim() || null
            : null,
        furnishingItems: {
          ...Object.fromEntries(FURNISHING_ITEM_KEYS.map((k) => [k, Number(furnishingItems[k]) || 0])),
          ...Object.fromEntries(customItems.filter((c) => c.name.trim()).map((c) => [c.name.trim(), Number(c.qty) || 0])),
        },
      });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t("common.somethingWrong"));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="card" style={{ background: "var(--surface-alt)", marginBottom: 10 }}>
      {error && <div className="error-banner">{error}</div>}
      <div className="field">
        <label>{t("addPropertyForm.houseNameLabel")}</label>
        <div className="input-wrap"><input value={label} onChange={(e) => setLabel(e.target.value)} /></div>
      </div>
      <div className="field">
        <label>{t("addPropertyForm.typeLabel")}</label>
        <Select
          value={type}
          onChange={setType}
          options={[...RESIDENTIAL_TYPES, ...COMMERCIAL_TYPES].map((pt) => ({ value: pt, label: pt }))}
        />
      </div>
      <div style={{ display: "flex", gap: 8 }}>
        <div className="field" style={{ flex: 1 }}>
          <label>{t("addPropertyForm.expectedRentLabel")}</label>
          <div className="input-wrap"><input type="number" value={expectedRent} onChange={(e) => setExpectedRent(e.target.value)} /></div>
        </div>
        <div className="field" style={{ flex: 1 }}>
          <label>{t("addPropertyForm.expectedDepositLabel")}</label>
          <div className="input-wrap"><input type="number" value={expectedDeposit} onChange={(e) => setExpectedDeposit(e.target.value)} /></div>
        </div>
      </div>
      <div className="field">
        <label>{t("addPropertyForm.maintenanceLabel")}</label>
        <div className="input-wrap"><input type="number" value={maintenance} onChange={(e) => setMaintenance(e.target.value)} placeholder={t("addPropertyForm.maintenancePlaceholder")} /></div>
      </div>

      <div className="field">
        <label>{t("editPropertyForm.furnishingStatusLabel")}</label>
        <div className="chip-row">
          {FURNISHING_STATUSES.map((f) => (
            <div
              key={f}
              className={`chip${furnishingStatus === f ? " selected" : ""}`}
              onClick={() => setFurnishingStatus(furnishingStatus === f ? null : f)}
            >
              {t(`editPropertyForm.${category === "commercial" ? "commercialFurnishing" : "furnishing"}_${f}`)}
            </div>
          ))}
        </div>
      </div>

      {category !== "commercial" && (
        <div className="field">
          <label>{t("editPropertyForm.communityTypeLabel")}</label>
          <div className="chip-row">
            {COMMUNITY_TYPES.map((c) => (
              <div
                key={c}
                className={`chip${communityType === c ? " selected" : ""}`}
                onClick={() => setCommunityType(communityType === c ? null : c)}
              >
                {t(`editPropertyForm.community_${c}`)}
              </div>
            ))}
          </div>
        </div>
      )}

      {(category === "commercial" || FLOOR_COMMUNITY_TYPES.includes(communityType ?? "")) && (
        <div className="field">
          <label>{t("editPropertyForm.floorNumberLabel")}</label>
          <div className="input-wrap">
            <input
              value={floorNumber}
              onChange={(e) => setFloorNumber(e.target.value)}
              placeholder={t("editPropertyForm.floorNumberPlaceholder")}
            />
          </div>
        </div>
      )}

      <div className="field">
        <label>{t("editPropertyForm.furnishingItemsLabel")}</label>
        <div className="field-hint" style={{ marginBottom: 8 }}>{t("editPropertyForm.furnishingItemsHint")}</div>
        <FurnishingItemsEditor
          presetItems={furnishingItems}
          onPresetChange={(key, value) => setFurnishingItems((prev) => ({ ...prev, [key]: value }))}
          removedPresets={removedPresets}
          onRemovedPresetsChange={setRemovedPresets}
          customItems={customItems}
          onCustomItemsChange={setCustomItems}
        />
      </div>

      <div style={{ display: "flex", gap: 8 }}>
        <button className="btn-outline btn-sm" style={{ flex: 1 }} onClick={onCancel}>
          {t("common.cancel")}
        </button>
        <button className="btn-solid btn-sm" style={{ flex: 1 }} disabled={busy} onClick={submit}>
          {busy ? t("common.saving") : t("common.save")}
        </button>
      </div>
    </div>
  );
}

function UnitRow({
  unit,
  propertyLabel,
  category,
  onChanged,
  onNavigate,
}: {
  unit: PropertyUnit;
  propertyLabel: string;
  category?: "independent" | "pg" | "commercial";
  onChanged: () => void;
  onNavigate?: (view: "plans" | "refer") => void;
}) {
  const [open, setOpen] = useState(false);
  const [assigning, setAssigning] = useState(false);
  const [editingTenant, setEditingTenant] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [editingUnit, setEditingUnit] = useState(false);
  const [promptDismissed, setPromptDismissed] = useState(false);
  const [showAgreement, setShowAgreement] = useState(false);
  const [showLimitSheet, setShowLimitSheet] = useState(false);
  const toast = useToast();
  const { user } = useAuth();
  const { t } = useLanguage();

  async function deleteUnit() {
    if (!confirm(t("ownerListings.confirmDeleteHouse", { label: unit.label }))) return;
    try {
      await api.del(`/api/units/${unit.id}`);
      toast(t("ownerListings.houseRemoved"));
      onChanged();
    } catch (err) {
      toast(err instanceof ApiError ? err.message : t("common.somethingWrong"));
    }
  }

  async function toggleUnitToLet() {
    try {
      await api.patch(`/api/units/${unit.id}`, { toLet: !unit.toLet });
      toast(!unit.toLet ? t("ownerListings.listedOnMarketplace") : t("ownerListings.removedFromMarketplace"));
      onChanged();
    } catch (err) {
      if (err instanceof ApiError && err.status === 402) {
        setShowLimitSheet(true);
      } else {
        toast(err instanceof ApiError ? err.message : t("ownerListings.couldNotUpdateListing"));
      }
    }
  }

  if (editingUnit) {
    return (
      <UnitForm
        unit={unit}
        category={category}
        onCancel={() => setEditingUnit(false)}
        onSubmit={async (form) => {
          await api.patch(`/api/units/${unit.id}`, form);
          setEditingUnit(false);
          toast(t("ownerListings.houseUpdated"));
          onChanged();
        }}
      />
    );
  }

  if (showAgreement && unit.tenancy) {
    return (
      <div className="doc-overlay">
        <RentalAgreement
          tenancyId={unit.tenancy.id}
          tenantName={unit.tenancy.name}
          tenantPhone={unit.tenancy.phone}
          ownerName={user?.name}
          ownerPhone={user?.phone}
          propertyLabel={`${propertyLabel} (${unit.label})`}
          rent={unit.tenancy.rent}
          deposit={unit.tenancy.deposit}
          startDate={unit.tenancy.startDate}
          onBack={() => setShowAgreement(false)}
        />
      </div>
    );
  }

  return (
    <div className="card" style={{ background: "var(--surface-alt)", marginBottom: 8 }}>
      <div className="card-row" onClick={() => setOpen((v) => !v)} style={{ cursor: "pointer" }}>
        <div>
          <b>{unit.label}</b>
          <div className="sub">{unit.type} &middot; {fmtINR(unit.expectedRent)}{t("ownerListings.perMonth")}</div>
          {unit.maintenance ? <div className="maintenance-line">{t("ownerHome.maintenanceLine", { amount: fmtINR(unit.maintenance) })}</div> : null}
        </div>
        <span className={`badge ${unit.tenancy ? "occupied" : "vacant"}`}>
          {unit.tenancy ? t("ownerListings.occupiedBadge") : t("ownerListings.vacantBadge")}
        </span>
      </div>
      {open && (
        <div style={{ marginTop: 10 }}>
          {unit.tenancy && unit.tenancy.onboardingStatus === "pending" && unit.tenancy.phone && !promptDismissed && (
            <InstallAppPrompt
              tenantName={unit.tenancy.name}
              tenantPhone={unit.tenancy.phone}
              propertyLabel={`${propertyLabel} (${unit.label})`}
              hasAccount={!!unit.tenancy.tenantId}
              onDismiss={() => setPromptDismissed(true)}
            />
          )}
          {unit.tenancy && (
            <button className="btn-outline btn-sm" style={{ width: "100%", marginBottom: 10 }} onClick={() => setShowAgreement(true)}>
              {t("ownerListings.rentalAgreementButton")} <span className="mi-right">›</span>
            </button>
          )}
          {unit.tenancy ? (
            editingTenant ? (
              <EditTenantForm
                tenancy={unit.tenancy}
                onCancel={() => setEditingTenant(false)}
                onSubmit={async (form) => {
                  await api.patch(`/api/tenancies/${unit.tenancy!.id}`, form);
                  setEditingTenant(false);
                  toast(t("ownerListings.tenantUpdated"));
                  onChanged();
                }}
              />
            ) : removing ? (
              <EndTenancyForm
                tenancy={unit.tenancy}
                onCancel={() => setRemoving(false)}
                onDone={() => {
                  setRemoving(false);
                  onChanged();
                }}
              />
            ) : (
              <>
                <div className="card-row">
                  <div>
                    <b>{unit.tenancy.name}</b>
                    <div className="sub">{unit.tenancy.phone || t("ownerListings.noPhoneOnFile")}</div>
                  </div>
                  <span className="badge occupied">{fmtINR(unit.tenancy.rent)}{t("ownerListings.perMonth")}</span>
                </div>
                <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
                  <button className="btn-outline btn-sm" style={{ flex: 1 }} onClick={() => setEditingTenant(true)}>
                    {t("ownerListings.editTenantButton")}
                  </button>
                  <button className="btn-outline danger btn-sm" style={{ flex: 1 }} onClick={() => setRemoving(true)}>
                    {t("ownerListings.endTenancyButton")}
                  </button>
                </div>
              </>
            )
          ) : assigning ? (
            <AssignTenantForm
              defaultRent={unit.expectedRent}
              defaultDeposit={unit.expectedDeposit}
              onSubmit={async (form) => {
                await api.post(`/api/units/${unit.id}/tenant`, form);
                setAssigning(false);
                toast(t("ownerListings.tenantAdded"));
                onChanged();
              }}
              onCancel={() => setAssigning(false)}
            />
          ) : (
            <>
              <div className="vacant-row" style={{ marginBottom: 10 }}>
                <div className="vacant-info">
                  <div className="vacant-label">{t("ownerListings.listOnToLet")}</div>
                  <div className="vacant-sub">
                    {unit.toLet ? (<><span className="live-dot" /> {t("ownerListings.liveVisible")}</>) : t("ownerListings.notListedYet")}
                  </div>
                </div>
                <button
                  className={`toggle${unit.toLet ? " on" : ""}`}
                  onClick={toggleUnitToLet}
                  aria-label={t("ownerListings.toggleToLetAria")}
                >
                  <i />
                </button>
              </div>
              <div style={{ display: "flex", gap: 8 }}>
                <button className="btn-outline btn-sm" style={{ flex: 1 }} onClick={() => setAssigning(true)}>
                  {t("ownerListings.addTenantLink")}
                </button>
                <button className="btn-outline btn-sm" style={{ flex: 1 }} onClick={() => setEditingUnit(true)}>
                  {t("ownerListings.editDetails")}
                </button>
                <button className="btn-outline danger btn-sm" style={{ flex: 1 }} onClick={deleteUnit}>
                  {t("ownerListings.deleteButton")}
                </button>
              </div>
            </>
          )}
        </div>
      )}
      {showLimitSheet && <ToLetLimitSheet onClose={() => setShowLimitSheet(false)} onNavigate={onNavigate} />}
    </div>
  );
}

function PgDetail({ property, onChanged }: { property: Property; onChanged: () => void }) {
  const rooms = property.rooms ?? [];
  const categories = new Map<string, typeof rooms>();
  for (const room of rooms) {
    if (!categories.has(room.sharingType)) categories.set(room.sharingType, []);
    categories.get(room.sharingType)!.push(room);
  }
  const [adding, setAdding] = useState(false);
  const { t } = useLanguage();

  return (
    <div>
      {[...categories.entries()].map(([sharingType, roomsInCategory]) => (
        <PgCategorySection
          key={sharingType}
          sharingType={sharingType}
          rooms={roomsInCategory}
          property={property}
          onChanged={onChanged}
        />
      ))}
      {adding ? (
        <AddRoomForm
          property={property}
          onDone={() => {
            setAdding(false);
            onChanged();
          }}
          onCancel={() => setAdding(false)}
        />
      ) : (
        <div className="section-title" style={{ margin: rooms.length ? "10px 0 0" : 0 }}>
          {t("addPropertyForm.roomsHeading")}
          <small onClick={() => setAdding(true)}>{t("addPropertyForm.addRoomLink")}</small>
        </div>
      )}
    </div>
  );
}

function AddRoomForm({
  property,
  onDone,
  onCancel,
}: {
  property: Property;
  onDone: () => void;
  onCancel: () => void;
}) {
  const [sharingType, setSharingType] = useState("Double Sharing");
  const [rentPerBed, setRentPerBed] = useState("");
  const [depositPerBed, setDepositPerBed] = useState("");
  const [bedCount, setBedCount] = useState("2");
  const [hasAc, setHasAc] = useState(false);
  const [hasAttachedBathroom, setHasAttachedBathroom] = useState(false);
  const [busy, setBusy] = useState(false);
  const toast = useToast();
  const { t } = useLanguage();

  async function submit() {
    setBusy(true);
    try {
      await api.post(`/api/properties/${property.id}/rooms`, {
        sharingType,
        rentPerBed: Number(rentPerBed) || 0,
        depositPerBed: Number(depositPerBed) || 0,
        bedCount: Number(bedCount) || 1,
        hasAc,
        hasAttachedBathroom,
      });
      toast(t("ownerListings.roomAdded"));
      onDone();
    } catch (err) {
      toast(err instanceof ApiError ? err.message : t("ownerListings.couldNotAddRoom"));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="card" style={{ background: "var(--surface-alt)" }}>
      <div className="field" style={{ marginBottom: 8 }}>
        <label>{t("addPropertyForm.sharingTypeLabel")}</label>
        <Select
          value={sharingType}
          onChange={setSharingType}
          options={["Single", "Double Sharing", "Triple Sharing", "Four Sharing"].map((s) => ({ value: s, label: s }))}
        />
      </div>
      <div style={{ display: "flex", gap: 8 }}>
        <div className="field" style={{ flex: 1, marginBottom: 0 }}>
          <label>{t("addPropertyForm.rentPerBedLabel")}</label>
          <div className="input-wrap"><input type="number" value={rentPerBed} onChange={(e) => setRentPerBed(e.target.value)} /></div>
        </div>
        <div className="field" style={{ flex: 1, marginBottom: 0 }}>
          <label>{t("addPropertyForm.depositPerBedLabel")}</label>
          <div className="input-wrap"><input type="number" value={depositPerBed} onChange={(e) => setDepositPerBed(e.target.value)} /></div>
        </div>
        <div className="field" style={{ flex: 1, marginBottom: 0 }}>
          <label>{t("addPropertyForm.bedsLabel")}</label>
          <div className="input-wrap"><input type="number" value={bedCount} onChange={(e) => setBedCount(e.target.value)} /></div>
        </div>
      </div>
      <div className="chip-row" style={{ marginTop: 8 }}>
        <div className={`chip${hasAc ? " selected" : ""}`} onClick={() => setHasAc((v) => !v)}>
          {t("addPropertyForm.roomAc")}
        </div>
        <div className={`chip${hasAttachedBathroom ? " selected" : ""}`} onClick={() => setHasAttachedBathroom((v) => !v)}>
          {t("addPropertyForm.roomAttachedBathroom")}
        </div>
      </div>
      <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
        <button className="btn-outline btn-sm" style={{ flex: 1 }} onClick={onCancel} disabled={busy}>
          {t("common.cancel")}
        </button>
        <button className="btn-solid btn-sm" style={{ flex: 1 }} onClick={submit} disabled={busy}>
          {busy ? t("common.saving") : t("common.save")}
        </button>
      </div>
    </div>
  );
}

// Beds are grouped by sharing type (Single/Double/Triple/...) and collapsed
// by default — a PG with dozens of beds per category would otherwise dump
// every single bed row into one long scroll just to add one tenant.
function PgCategorySection({
  sharingType,
  rooms,
  property,
  onChanged,
}: {
  sharingType: string;
  rooms: NonNullable<Property["rooms"]>;
  property: Property;
  onChanged: () => void;
}) {
  const [open, setOpen] = useState(false);
  const { t } = useLanguage();
  const totalBeds = rooms.reduce((n, r) => n + r.beds.length, 0);
  const occupiedBeds = rooms.reduce((n, r) => n + r.beds.filter((b) => b.tenancy).length, 0);
  const sameRent = rooms.every((r) => r.rentPerBed === rooms[0].rentPerBed);

  return (
    <div className="card" style={{ marginBottom: 10 }}>
      <div className="card-row" onClick={() => setOpen((v) => !v)} style={{ cursor: "pointer" }}>
        <div>
          <b>{sharingType}</b>
          <div className="sub">{sameRent ? t("ownerListings.perBed", { amount: fmtINR(rooms[0].rentPerBed) }) : t("ownerListings.priceTiers", { count: rooms.length })}</div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <span className="badge vacant">
            {t("ownerListings.occupiedFraction", { occupied: occupiedBeds, total: totalBeds })}
          </span>
          <span style={{ transform: open ? "rotate(90deg)" : "none", transition: "transform .15s ease", color: "var(--ink-faint)" }}>
            ›
          </span>
        </div>
      </div>
      {open && (
        <div style={{ marginTop: 12 }}>
          {rooms.map((room) => (
            <div key={room.id}>
              {!sameRent && (
                <div className="sub" style={{ margin: "10px 0 6px" }}>{t("ownerListings.perBed", { amount: fmtINR(room.rentPerBed) })}</div>
              )}
              {room.beds.map((bed) => (
                <BedRow key={bed.id} bed={bed} room={room} property={property} onChanged={onChanged} />
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function BedRow({
  bed,
  room,
  property,
  onChanged,
}: {
  bed: any;
  room: any;
  property: Property;
  onChanged: () => void;
}) {
  const [assigning, setAssigning] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [editingTenant, setEditingTenant] = useState(false);
  const [showAgreement, setShowAgreement] = useState(false);
  const [showReceipts, setShowReceipts] = useState(false);
  const [receiptEntries, setReceiptEntries] = useState<ReceiptEntry[] | null>(null);
  const [viewingReceipt, setViewingReceipt] = useState<ReceiptEntry | null>(null);
  const [promptDismissed, setPromptDismissed] = useState(false);
  const toast = useToast();
  const { user } = useAuth();
  const { t } = useLanguage();

  async function openReceipts() {
    setShowReceipts(true);
    if (receiptEntries === null) {
      try {
        const { entries } = await api.get(`/api/ledger/tenancy/${bed.tenancy.id}`);
        setReceiptEntries(entries.filter((e: ReceiptEntry & { status: string }) => e.status === "paid"));
      } catch {
        setReceiptEntries([]);
      }
    }
  }

  if (viewingReceipt && bed.tenancy) {
    return (
      <div className="doc-overlay">
        <RentReceipt
          entry={viewingReceipt}
          tenantName={bed.tenancy.name}
          propertyLabel={`${property.label} (${bed.label})`}
          propertyLocality={property.locality}
          ownerName={user?.name}
          onBack={() => setViewingReceipt(null)}
        />
      </div>
    );
  }

  if (showReceipts && bed.tenancy) {
    return (
      <div className="doc-overlay">
        <button className="link-btn" style={{ marginBottom: 12 }} onClick={() => setShowReceipts(false)}>
          {t("ownerListings.receiptsBackLink")}
        </button>
        <div className="section-title">{t("ownerListings.rentalReceiptsButton")}</div>
        {receiptEntries === null ? (
          <div className="sub">{t("ownerListings.loadingReceipts")}</div>
        ) : receiptEntries.length === 0 ? (
          <div className="sub">{t("ownerListings.noReceiptsYet")}</div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {receiptEntries.map((entry) => (
              <div
                key={entry.id}
                className="card"
                style={{ cursor: "pointer", display: "flex", justifyContent: "space-between", alignItems: "center" }}
                onClick={() => setViewingReceipt(entry)}
              >
                <b>{fmtMonth(entry.month)}</b>
                <span className="sub">{fmtINR(entry.amount)}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  if (showAgreement && bed.tenancy) {
    return (
      <div className="doc-overlay">
        <RentalAgreement
          tenancyId={bed.tenancy.id}
          tenantName={bed.tenancy.name}
          tenantPhone={bed.tenancy.phone}
          ownerName={user?.name}
          ownerPhone={user?.phone}
          propertyLabel={property.label}
          propertyLocality={property.locality}
          propertyType={`${room.sharingType} · ${bed.label}`}
          rent={bed.tenancy.rent}
          deposit={bed.tenancy.deposit}
          startDate={bed.tenancy.startDate}
          onBack={() => setShowAgreement(false)}
        />
      </div>
    );
  }

  return (
    <div className="card" style={{ background: "var(--surface-alt)", marginBottom: 8 }}>
      <div className="card-row">
        <div>
          {bed.tenancy ? (
            <>
              <div className="sub">{bed.label}</div>
              <b style={{ fontSize: 17 }}>{bed.tenancy.name}</b>
            </>
          ) : (
            <b>{bed.label}</b>
          )}
        </div>
        {bed.tenancy ? (
          <span className="badge occupied">{t("ownerListings.occupiedBadge")}</span>
        ) : (
          <span className="badge vacant">{t("ownerListings.vacantBadge")}</span>
        )}
      </div>
      {!bed.tenancy && (
        <div className="vacant-row" style={{ marginTop: 10 }}>
          <div className="vacant-info">
            <div className="vacant-label">{t("ownerHome.listOnToLet")}</div>
            <div className="vacant-sub">
              {bed.toLet ? (<><span className="live-dot" /> {t("ownerHome.liveVisible")}</>) : t("ownerHome.notListedYet")}
            </div>
          </div>
          <button
            className={`toggle${bed.toLet ? " on" : ""}`}
            onClick={async (e) => {
              e.stopPropagation();
              try {
                await api.patch(`/api/beds/${bed.id}`, { toLet: !bed.toLet });
                onChanged();
              } catch (err) {
                toast(err instanceof ApiError ? err.message : t("common.somethingWrong"));
              }
            }}
            aria-label={t("ownerHome.toggleToLetAria")}
          >
            <i />
          </button>
        </div>
      )}
      {bed.tenancy && bed.tenancy.onboardingStatus === "pending" && bed.tenancy.phone && !promptDismissed && (
        <InstallAppPrompt
          tenantName={bed.tenancy.name}
          tenantPhone={bed.tenancy.phone}
          propertyLabel={`${property.label} (${bed.label})`}
          hasAccount={!!bed.tenancy.tenantId}
          onDismiss={() => setPromptDismissed(true)}
        />
      )}
      {bed.tenancy ? (
        editingTenant ? (
          <EditTenantForm
            tenancy={bed.tenancy}
            onCancel={() => setEditingTenant(false)}
            onSubmit={async (form) => {
              await api.patch(`/api/tenancies/${bed.tenancy.id}`, form);
              setEditingTenant(false);
              toast(t("ownerListings.tenantUpdated"));
              onChanged();
            }}
          />
        ) : removing ? (
          <EndTenancyForm
            tenancy={bed.tenancy}
            onCancel={() => setRemoving(false)}
            onDone={() => {
              setRemoving(false);
              onChanged();
            }}
          />
        ) : (
          <div style={{ display: "flex", gap: 8, marginTop: 8, flexWrap: "wrap" }}>
            <button className="btn-outline btn-sm" style={{ flex: 1 }} onClick={() => setShowAgreement(true)}>
              {t("ownerListings.rentalAgreementButton")}
            </button>
            <button className="btn-outline btn-sm" style={{ flex: 1 }} onClick={openReceipts}>
              {t("ownerListings.rentalReceiptsButton")}
            </button>
            <button className="btn-outline btn-sm" style={{ flex: 1 }} onClick={() => setEditingTenant(true)}>
              {t("ownerListings.editTenantButton")}
            </button>
            <button className="btn-outline danger btn-sm" style={{ flex: 1 }} onClick={() => setRemoving(true)}>
              {t("ownerListings.endTenancyButton")}
            </button>
          </div>
        )
      ) : assigning ? (
        <AssignTenantForm
          defaultRent={room.rentPerBed}
          defaultDeposit={room.depositPerBed}
          onSubmit={async (form) => {
            await api.post(`/api/beds/${bed.id}/tenant`, form);
            setAssigning(false);
            toast(t("ownerListings.tenantAdded"));
            onChanged();
          }}
          onCancel={() => setAssigning(false)}
        />
      ) : (
        <button className="btn-outline btn-sm" style={{ marginTop: 8 }} onClick={() => setAssigning(true)}>
          {t("ownerListings.addTenantLink")}
        </button>
      )}
    </div>
  );
}

function AssignTenantForm({
  defaultRent,
  defaultDeposit,
  onSubmit,
  onCancel,
}: {
  defaultRent?: number | null;
  defaultDeposit?: number | null;
  onSubmit: (form: {
    name: string;
    phone: string;
    aadhaar: string;
    aadhaarPhotoFront: string;
    aadhaarPhotoBack: string;
    rent: number;
    deposit: number;
    rentDueDay: number;
    noticePeriodDays: number;
    startDate: string;
  }) => Promise<void>;
  onCancel: () => void;
}) {
  const [mode, setMode] = useState<"new" | "existing">("new");
  const [existingTenants, setExistingTenants] = useState<{ name: string; phone: string; aadhaar: string }[] | null>(null);
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [aadhaar, setAadhaar] = useState("");
  const [aadhaarPhotoFront, setAadhaarPhotoFront] = useState("");
  const [aadhaarPhotoBack, setAadhaarPhotoBack] = useState("");
  const [rent, setRent] = useState(String(defaultRent ?? ""));
  const [deposit, setDeposit] = useState(String(defaultDeposit ?? ""));
  const [rentDueDay, setRentDueDay] = useState("5");
  const [noticePeriodDays, setNoticePeriodDays] = useState("60");
  const [startDate, setStartDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const { t } = useLanguage();

  function switchMode(next: "new" | "existing") {
    setMode(next);
    setError("");
    if (next === "existing" && existingTenants === null) {
      api
        .get("/api/tenants/mine")
        .then(({ tenants }) => setExistingTenants(tenants))
        .catch(() => setExistingTenants([]));
    }
  }

  function pickExisting(t: { name: string; phone: string; aadhaar: string }) {
    setName(t.name);
    setPhone(t.phone ?? "");
    setAadhaar(t.aadhaar ?? "");
  }

  async function submit() {
    if (!name.trim()) return setError(t("assignTenantForm.errNameRequired"));
    // A phone number is what makes a tenant a real invite, not just a
    // record — it's what the "share on WhatsApp" / "notify tenant" prompt
    // needs to fire, and it's how an existing account auto-links. Without
    // one, a newly added tenant would silently have no way to be told
    // they've been added at all.
    if (!/^[6-9]\d{9}$/.test(phone.trim())) return setError(t("assignTenantForm.errPhoneRequired"));
    setBusy(true);
    setError("");
    try {
      await onSubmit({
        name,
        phone,
        aadhaar,
        aadhaarPhotoFront,
        aadhaarPhotoBack,
        rent: Number(rent) || 0,
        deposit: Number(deposit) || 0,
        rentDueDay: Math.min(31, Math.max(1, Number(rentDueDay) || 5)),
        noticePeriodDays: Math.min(365, Math.max(0, Number(noticePeriodDays) || 60)),
        startDate,
      });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t("common.somethingWrong"));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div style={{ marginTop: 8 }}>
      {error && <div className="error-banner">{error}</div>}
      <div className="chip-row" style={{ marginBottom: 12 }}>
        <div className={`chip${mode === "new" ? " selected" : ""}`} onClick={() => switchMode("new")}>
          {t("assignTenantForm.newTenant")}
        </div>
        <div className={`chip${mode === "existing" ? " selected" : ""}`} onClick={() => switchMode("existing")}>
          {t("assignTenantForm.existingTenant")}
        </div>
      </div>

      {mode === "existing" && (
        <div style={{ marginBottom: 12 }}>
          {existingTenants === null ? (
            <div className="spinner" />
          ) : existingTenants.length === 0 ? (
            <div className="empty-state">{t("assignTenantForm.emptyExistingTenants")}</div>
          ) : (
            existingTenants.map((et, i) => (
              <div
                key={`${et.phone}-${i}`}
                className="card"
                style={{ background: name === et.name && phone === (et.phone ?? "") ? "var(--brand-100)" : "var(--surface-alt)", marginBottom: 8, cursor: "pointer" }}
                onClick={() => pickExisting(et)}
              >
                <div className="card-row">
                  <div>
                    <b>{et.name}</b>
                    <div className="sub">{et.phone || t("assignTenantForm.noPhoneOnFile")}</div>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      )}

      <div className="field">
        <div className="field-label-row">
          <label>{t("assignTenantForm.tenantNameLabel")}</label>
          <ContactPickerButton onPick={(c) => { setName(c.name); setPhone(c.phone); }} />
        </div>
        <div className="input-wrap"><input value={name} onChange={(e) => setName(e.target.value)} /></div>
      </div>
      <div className="field">
        <label>{t("assignTenantForm.phoneLabel")}</label>
        <div className="input-wrap"><input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder={t("assignTenantForm.phonePlaceholder")} /></div>
      </div>
      <div className="field">
        <label>{t("assignTenantForm.aadhaarLabel")}</label>
        <div className="input-wrap"><input value={aadhaar} onChange={(e) => setAadhaar(e.target.value)} /></div>
      </div>
      <AadhaarPhotoFields
        front={aadhaarPhotoFront}
        back={aadhaarPhotoBack}
        onChangeFront={setAadhaarPhotoFront}
        onChangeBack={setAadhaarPhotoBack}
      />
      <div style={{ display: "flex", gap: 10 }}>
        <div className="field" style={{ flex: 1 }}>
          <label>{t("assignTenantForm.rentLabel")}</label>
          <div className="input-wrap"><input type="number" value={rent} onChange={(e) => setRent(e.target.value)} /></div>
        </div>
        <div className="field" style={{ flex: 1 }}>
          <label>{t("assignTenantForm.depositLabel")}</label>
          <div className="input-wrap"><input type="number" value={deposit} onChange={(e) => setDeposit(e.target.value)} /></div>
        </div>
      </div>
      <div className="field">
        <label>{t("assignTenantForm.dateOfOccupancyLabel")}</label>
        <div className="input-wrap"><input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} /></div>
        <div className="field-hint">{t("assignTenantForm.dateHint")}</div>
      </div>
      <div className="field">
        <label>{t("assignTenantForm.rentDueDayLabel")}</label>
        <div className="input-wrap">
          <input type="number" min={1} max={31} value={rentDueDay} onChange={(e) => setRentDueDay(e.target.value)} />
        </div>
        <div className="field-hint">{t("assignTenantForm.rentDueDayHint")}</div>
      </div>
      <div className="field">
        <label>{t("assignTenantForm.noticePeriodLabel")}</label>
        <div className="input-wrap">
          <input type="number" min={0} max={365} value={noticePeriodDays} onChange={(e) => setNoticePeriodDays(e.target.value)} />
        </div>
        <div className="field-hint">{t("assignTenantForm.noticePeriodHint")}</div>
      </div>
      <div style={{ display: "flex", gap: 8 }}>
        <button className="btn-outline btn-sm" style={{ flex: 1 }} onClick={onCancel}>
          {t("common.cancel")}
        </button>
        <button className="btn-solid btn-sm" style={{ flex: 1 }} disabled={busy} onClick={submit}>
          {busy ? t("common.saving") : t("assignTenantForm.addTenantButton")}
        </button>
      </div>
    </div>
  );
}

function EditTenantForm({
  tenancy,
  onSubmit,
  onCancel,
}: {
  tenancy: Tenancy;
  onSubmit: (form: {
    name: string;
    phone: string;
    aadhaar: string;
    aadhaarPhotoFront: string;
    aadhaarPhotoBack: string;
    rent: number;
    deposit: number;
    rentDueDay: number;
    noticePeriodDays: number;
  }) => Promise<void>;
  onCancel: () => void;
}) {
  const [name, setName] = useState(tenancy.name);
  const [phone, setPhone] = useState(tenancy.phone ?? "");
  const [aadhaar, setAadhaar] = useState(tenancy.aadhaar ?? "");
  const [aadhaarPhotoFront, setAadhaarPhotoFront] = useState(tenancy.aadhaarPhotoFront ?? "");
  const [aadhaarPhotoBack, setAadhaarPhotoBack] = useState(tenancy.aadhaarPhotoBack ?? "");
  const [rent, setRent] = useState(String(tenancy.rent));
  const [deposit, setDeposit] = useState(String(tenancy.deposit));
  const [rentDueDay, setRentDueDay] = useState(String(tenancy.rentDueDay));
  const [noticePeriodDays, setNoticePeriodDays] = useState(String(tenancy.noticePeriodDays));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const { t } = useLanguage();

  async function submit() {
    if (!name.trim()) return setError(t("assignTenantForm.errNameRequired"));
    setBusy(true);
    setError("");
    try {
      await onSubmit({
        name,
        phone,
        aadhaar,
        aadhaarPhotoFront,
        aadhaarPhotoBack,
        rent: Number(rent) || 0,
        deposit: Number(deposit) || 0,
        rentDueDay: Math.min(31, Math.max(1, Number(rentDueDay) || 5)),
        noticePeriodDays: Math.min(365, Math.max(0, Number(noticePeriodDays) || 60)),
      });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t("common.somethingWrong"));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div style={{ marginTop: 8 }}>
      {error && <div className="error-banner">{error}</div>}
      <div className="field">
        <div className="field-label-row">
          <label>{t("assignTenantForm.tenantNameLabel")}</label>
          <ContactPickerButton onPick={(c) => { setName(c.name); setPhone(c.phone); }} />
        </div>
        <div className="input-wrap"><input value={name} onChange={(e) => setName(e.target.value)} /></div>
      </div>
      <div className="field">
        <label>{t("assignTenantForm.phoneLabel")}</label>
        <div className="input-wrap"><input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder={t("assignTenantForm.phonePlaceholder")} /></div>
      </div>
      <div className="field">
        <label>{t("assignTenantForm.aadhaarLabel")}</label>
        <div className="input-wrap"><input value={aadhaar} onChange={(e) => setAadhaar(e.target.value)} /></div>
      </div>
      <AadhaarPhotoFields
        front={aadhaarPhotoFront}
        back={aadhaarPhotoBack}
        onChangeFront={setAadhaarPhotoFront}
        onChangeBack={setAadhaarPhotoBack}
      />
      <div style={{ display: "flex", gap: 10 }}>
        <div className="field" style={{ flex: 1 }}>
          <label>{t("assignTenantForm.rentLabel")}</label>
          <div className="input-wrap"><input type="number" value={rent} onChange={(e) => setRent(e.target.value)} /></div>
        </div>
        <div className="field" style={{ flex: 1 }}>
          <label>{t("assignTenantForm.depositLabel")}</label>
          <div className="input-wrap"><input type="number" value={deposit} onChange={(e) => setDeposit(e.target.value)} /></div>
        </div>
      </div>
      <div className="field">
        <label>{t("assignTenantForm.rentDueDayLabel")}</label>
        <div className="input-wrap">
          <input type="number" min={1} max={31} value={rentDueDay} onChange={(e) => setRentDueDay(e.target.value)} />
        </div>
        <div className="field-hint">{t("assignTenantForm.rentDueDayHint")}</div>
      </div>
      <div className="field">
        <label>{t("assignTenantForm.noticePeriodLabel")}</label>
        <div className="input-wrap">
          <input type="number" min={0} max={365} value={noticePeriodDays} onChange={(e) => setNoticePeriodDays(e.target.value)} />
        </div>
        <div className="field-hint">{t("assignTenantForm.noticePeriodHint")}</div>
      </div>
      <div style={{ display: "flex", gap: 8 }}>
        <button className="btn-outline btn-sm" style={{ flex: 1 }} onClick={onCancel}>
          {t("common.cancel")}
        </button>
        <button className="btn-solid btn-sm" style={{ flex: 1 }} disabled={busy} onClick={submit}>
          {busy ? t("common.saving") : t("ownerListings.saveTenantButton")}
        </button>
      </div>
    </div>
  );
}

interface RoomDraft {
  sharingType: string;
  rentPerBed: string;
  depositPerBed: string;
  bedCount: string;
  hasAc: boolean;
  hasAttachedBathroom: boolean;
}

const MEAL_OPTIONS = ["breakfast", "lunch", "dinner"];
const TENANT_TYPE_OPTIONS = ["student", "professional"];
const FURNISHING_STATUSES = ["unfurnished", "semi_furnished", "fully_furnished"] as const;
const COMMUNITY_TYPES = ["gated", "semi_gated", "villa", "apartment", "independent"] as const;
const FLOOR_COMMUNITY_TYPES = ["gated", "semi_gated", "apartment"];

interface UnitDraft {
  label: string;
  type: string;
  expectedRent: string;
  expectedDeposit: string;
  maintenance: string;
  furnishingStatus: string | null;
  communityType: string | null;
  floorNumber: string;
  furnishingItems: Record<string, string>;
  customItems: CustomItemDraft[];
  removedPresetItems: string[];
  hasExistingTenant: boolean;
  tenantName: string;
  tenantPhone: string;
  tenantAadhaar: string;
  tenantAadhaarPhotoFront: string;
  tenantAadhaarPhotoBack: string;
  tenantStartDate: string;
  tenantRentDueDay: string;
  tenantNoticePeriodDays: string;
}

const RESIDENTIAL_TYPES = ["1RK", "1BHK", "2BHK", "3BHK", "4BHK", "5BHK", "6BHK", "Duplex", "Villa"];
const COMMERCIAL_TYPES = ["Shop", "Office", "Showroom", "Warehouse", "Co-working", "Factory"];

function blankUnit(label: string): UnitDraft {
  return {
    label,
    type: "2BHK",
    expectedRent: "",
    expectedDeposit: "",
    maintenance: "",
    furnishingStatus: null,
    communityType: null,
    floorNumber: "",
    furnishingItems: Object.fromEntries(FURNISHING_ITEM_KEYS.map((k) => [k, ""])),
    customItems: [],
    removedPresetItems: [...FURNISHING_ITEM_KEYS],
    hasExistingTenant: false,
    tenantName: "",
    tenantPhone: "",
    tenantAadhaar: "",
    tenantAadhaarPhotoFront: "",
    tenantAadhaarPhotoBack: "",
    tenantStartDate: new Date().toISOString().slice(0, 10),
    tenantRentDueDay: "5",
    tenantNoticePeriodDays: "60",
  };
}

type AddStep = "basics" | "location" | "typePricing" | "furnishing" | "details" | "houses" | "pgDetails" | "rooms" | "tenant" | "photos";

const AMENITY_PRESETS = ["wifi", "parking", "lift", "power_backup", "security", "gym"];

function AddPropertyForm({
  onDone,
  onCancel,
  onNavigate,
}: {
  onDone: () => void;
  onCancel: () => void;
  onNavigate?: (view: "plans") => void;
}) {
  const [category, setCategory] = useState<"independent" | "pg" | "commercial">("independent");
  const [label, setLabel] = useState("");
  const [locality, setLocality] = useState("");
  const [city, setCity] = useState("");
  const [state, setState] = useState("");
  const [pincode, setPincode] = useState("");
  const [type, setType] = useState("2BHK");
  const [occupancyType, setOccupancyType] = useState<"boys" | "girls" | "co-ed">("co-ed");
  const [mealPlan, setMealPlan] = useState<string[]>([]);
  const [preferredTenants, setPreferredTenants] = useState<string[]>([]);
  const [expectedRent, setExpectedRent] = useState("");
  const [expectedDeposit, setExpectedDeposit] = useState("");
  const [maintenance, setMaintenance] = useState("");
  const [furnishingStatus, setFurnishingStatus] = useState<string | null>(null);
  const [communityType, setCommunityType] = useState<string | null>(null);
  const [floorNumber, setFloorNumber] = useState("");
  const [furnishingItems, setFurnishingItems] = useState<Record<string, string>>(() =>
    Object.fromEntries(FURNISHING_ITEM_KEYS.map((k) => [k, ""]))
  );
  const [carpetAreaSqft, setCarpetAreaSqft] = useState("");
  const [builtUpAreaSqft, setBuiltUpAreaSqft] = useState("");
  const [lockInMonths, setLockInMonths] = useState("");
  const [rentEscalationPercent, setRentEscalationPercent] = useState("");
  const [rentEscalationYears, setRentEscalationYears] = useState("");
  const [powerBackup, setPowerBackup] = useState<boolean | null>(null);
  const [powerLoadDetails, setPowerLoadDetails] = useState("");
  const [parkingSpots, setParkingSpots] = useState("");
  const [washroomType, setWashroomType] = useState<string | null>(null);
  const [gstRegistrationPossible, setGstRegistrationPossible] = useState<boolean | null>(null);
  const [frontageSignage, setFrontageSignage] = useState<boolean | null>(null);
  const [possessionStatus, setPossessionStatus] = useState<string | null>(null);
  const [seatsCount, setSeatsCount] = useState("");
  const [cabinsCount, setCabinsCount] = useState("");
  const [meetingRoomsCount, setMeetingRoomsCount] = useState("");
  const [customItems, setCustomItems] = useState<CustomItemDraft[]>([]);
  // Brand-new property, no saved furnishing data — list opens empty.
  const [removedPresets, setRemovedPresets] = useState<string[]>([...FURNISHING_ITEM_KEYS]);
  const [rooms, setRooms] = useState<RoomDraft[]>([
    { sharingType: "Double Sharing", rentPerBed: "", depositPerBed: "", bedCount: "2", hasAc: false, hasAttachedBathroom: false },
  ]);
  const [multiUnit, setMultiUnit] = useState(false);
  const [units, setUnits] = useState<UnitDraft[]>([blankUnit("House 1")]);
  const [photos, setPhotos] = useState<string[]>([]);
  const [hasExistingTenant, setHasExistingTenant] = useState(false);
  const [tenantName, setTenantName] = useState("");
  const [tenantPhone, setTenantPhone] = useState("");
  const [tenantAadhaar, setTenantAadhaar] = useState("");
  const [tenantAadhaarPhotoFront, setTenantAadhaarPhotoFront] = useState("");
  const [tenantAadhaarPhotoBack, setTenantAadhaarPhotoBack] = useState("");
  const [tenantStartDate, setTenantStartDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [tenantRentDueDay, setTenantRentDueDay] = useState("5");
  const [tenantNoticePeriodDays, setTenantNoticePeriodDays] = useState("60");
  const [location, setLocation] = useState<PropertyLocation | null>(null);
  const [mapsKey, setMapsKey] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [errorIsPlanLimit, setErrorIsPlanLimit] = useState(false);
  // General amenities (independent, single-unit only) — this is new: the
  // form never had a way to set these before, only PG (hardcoded to
  // wifi+meals) and commercial (its own power/GST/frontage chips) did.
  const [amenities, setAmenities] = useState<string[]>([]);
  const [customAmenityInput, setCustomAmenityInput] = useState("");
  const [step, setStep] = useState<AddStep>("basics");
  const { t } = useLanguage();

  useEffect(() => {
    api.get("/api/config").then(({ googleMapsKey }) => setMapsKey(googleMapsKey)).catch(() => {});
  }, []);

  function updateRoom(i: number, patch: Partial<RoomDraft>) {
    setRooms((rs) => rs.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
  }

  function toggleInList(list: string[], setList: (v: string[]) => void, value: string) {
    setList(list.includes(value) ? list.filter((v) => v !== value) : [...list, value]);
  }

  function updateUnit(i: number, patch: Partial<UnitDraft>) {
    setUnits((us) => us.map((u, idx) => (idx === i ? { ...u, ...patch } : u)));
  }

  function toggleAmenity(value: string) {
    setAmenities((a) => (a.includes(value) ? a.filter((x) => x !== value) : [...a, value]));
  }
  function addCustomAmenity() {
    const v = customAmenityInput.trim();
    if (v && !amenities.includes(v)) setAmenities((a) => [...a, v]);
    setCustomAmenityInput("");
  }

  // Each category gets its own step sequence, built strictly from fields
  // that already exist for it — multi-unit independent/commercial collapse
  // type+pricing and furnishing into a single "houses" step because that's
  // how the per-house data is actually grouped (one repeating card per
  // house holds everything), not split across steps.
  const steps: AddStep[] =
    category === "pg"
      ? ["basics", "location", "pgDetails", "rooms", "photos"]
      : multiUnit
      ? ["basics", "location", "houses", "photos"]
      : category === "commercial"
      ? ["basics", "location", "typePricing", "details", "tenant", "photos"]
      : ["basics", "location", "typePricing", "furnishing", "tenant", "photos"];
  const stepIndex = steps.indexOf(step);
  const progress = Math.round(((stepIndex + 1) / steps.length) * 100);

  function goNext() {
    setError("");
    const i = steps.indexOf(step);
    if (i < steps.length - 1) setStep(steps[i + 1]);
  }
  function goBack() {
    setError("");
    const i = steps.indexOf(step);
    if (i > 0) setStep(steps[i - 1]);
    else onCancel();
  }
  function continueBasics() {
    if (!label.trim()) return setError(t("addPropertyForm.errNameRequired"));
    goNext();
  }
  function continueLocation() {
    if (!locality.trim()) return setError(t("addPropertyForm.errLocalityRequired"));
    if (!city.trim()) return setError(t("addPropertyForm.errCityRequired"));
    if (!state) return setError(t("addPropertyForm.errStateRequired"));
    if (!pincode.trim()) return setError(t("addPropertyForm.errPincodeRequired"));
    goNext();
  }
  function continueTypePricing() {
    if (!expectedRent.trim()) return setError(t("addPropertyForm.errRentRequired"));
    if (!expectedDeposit.trim()) return setError(t("addPropertyForm.errDepositRequired"));
    goNext();
  }
  function continueFurnishing() {
    if (!furnishingStatus) return setError(t("addPropertyForm.errFurnishingRequired"));
    if (!communityType) return setError(t("addPropertyForm.errCommunityTypeRequired"));
    goNext();
  }
  function continueDetails() {
    if (!furnishingStatus) return setError(t("addPropertyForm.errFurnishingRequired"));
    goNext();
  }

  async function submit() {
    if (!label.trim()) return setError(t("addPropertyForm.errNameRequired"));
    if (!locality.trim()) return setError(t("addPropertyForm.errLocalityRequired"));
    if (!city.trim()) return setError(t("addPropertyForm.errCityRequired"));
    if (!state) return setError(t("addPropertyForm.errStateRequired"));
    if (!pincode.trim()) return setError(t("addPropertyForm.errPincodeRequired"));
    // type is always non-empty (a preset dropdown with a default selection)
    // — furnishing/community/rent/deposit only apply to a single independent
    // or commercial property, not PG (rooms carry their own rent) or a
    // multi-house property (each house carries its own).
    if (category !== "pg" && !multiUnit) {
      if (!furnishingStatus) return setError(t("addPropertyForm.errFurnishingRequired"));
      if (category !== "commercial" && !communityType) return setError(t("addPropertyForm.errCommunityTypeRequired"));
      if (!expectedRent.trim()) return setError(t("addPropertyForm.errRentRequired"));
      if (!expectedDeposit.trim()) return setError(t("addPropertyForm.errDepositRequired"));
    }
    setBusy(true);
    setError("");
    try {
      const payload: any = {
        category,
        label,
        locality,
        city: city.trim() || null,
        state: state || null,
        pincode: pincode.trim() || null,
        toLet: true,
        location,
        // Multi-house properties carry furnishing per house instead (see
        // payload.units below) since it differs from house to house.
        furnishingStatus: category === "pg" || multiUnit ? null : furnishingStatus,
        communityType: category === "pg" || category === "commercial" || multiUnit ? null : communityType,
        floorNumber:
          category === "commercial" && !multiUnit
            ? floorNumber.trim() || null
            : category !== "pg" && !multiUnit && FLOOR_COMMUNITY_TYPES.includes(communityType ?? "")
            ? floorNumber.trim() || null
            : null,
        furnishingItems: category === "pg" || multiUnit
          ? {}
          : {
              ...Object.fromEntries(FURNISHING_ITEM_KEYS.map((k) => [k, Number(furnishingItems[k]) || 0])),
              ...Object.fromEntries(customItems.filter((c) => c.name.trim()).map((c) => [c.name.trim(), Number(c.qty) || 0])),
            },
      };
      const filteredUnits = units.filter((u) => u.label.trim());
      if (category !== "pg" && multiUnit) {
        payload.units = filteredUnits.map((u) => ({
          label: u.label,
          type: u.type,
          expectedRent: Number(u.expectedRent) || 0,
          expectedDeposit: Number(u.expectedDeposit) || 0,
          maintenance: Number(u.maintenance) || 0,
          furnishingStatus: u.furnishingStatus,
          communityType: category === "commercial" ? null : u.communityType,
          floorNumber:
            category === "commercial"
              ? u.floorNumber.trim() || null
              : FLOOR_COMMUNITY_TYPES.includes(u.communityType ?? "")
              ? u.floorNumber.trim() || null
              : null,
          furnishingItems: {
            ...Object.fromEntries(FURNISHING_ITEM_KEYS.map((k) => [k, Number(u.furnishingItems[k]) || 0])),
            ...Object.fromEntries(u.customItems.filter((c) => c.name.trim()).map((c) => [c.name.trim(), Number(c.qty) || 0])),
          },
        }));
      } else if (category !== "pg") {
        payload.type = type;
        payload.expectedRent = Number(expectedRent) || 0;
        payload.expectedDeposit = Number(expectedDeposit) || 0;
        payload.maintenance = Number(maintenance) || 0;
        if (category === "independent") {
          payload.facilities = amenities;
        }
        if (category === "commercial") {
          payload.carpetAreaSqft = carpetAreaSqft.trim() ? Number(carpetAreaSqft) : null;
          payload.builtUpAreaSqft = builtUpAreaSqft.trim() ? Number(builtUpAreaSqft) : null;
          payload.lockInMonths = lockInMonths.trim() ? Number(lockInMonths) : null;
          payload.rentEscalationPercent = rentEscalationPercent.trim() ? Number(rentEscalationPercent) : null;
          payload.rentEscalationYears = rentEscalationYears.trim() ? Number(rentEscalationYears) : null;
          payload.powerBackup = powerBackup;
          payload.powerLoadDetails = powerLoadDetails.trim() || null;
          payload.parkingSpots = parkingSpots.trim() ? Number(parkingSpots) : null;
          payload.washroomType = washroomType;
          payload.gstRegistrationPossible = gstRegistrationPossible;
          payload.frontageSignage = frontageSignage;
          payload.possessionStatus = possessionStatus;
          if (type === "Office" || type === "Co-working") {
            payload.seatsCount = seatsCount.trim() ? Number(seatsCount) : null;
            payload.cabinsCount = cabinsCount.trim() ? Number(cabinsCount) : null;
            payload.meetingRoomsCount = meetingRoomsCount.trim() ? Number(meetingRoomsCount) : null;
          }
        }
      } else {
        payload.occupancyType = occupancyType;
        payload.facilities = ["wifi", "meals"];
        payload.mealPlan = mealPlan;
        payload.preferredTenants = preferredTenants;
        payload.rooms = rooms.map((r) => ({
          sharingType: r.sharingType,
          rentPerBed: Number(r.rentPerBed) || 0,
          depositPerBed: Number(r.depositPerBed) || 0,
          bedCount: Number(r.bedCount) || 1,
          hasAc: r.hasAc,
          hasAttachedBathroom: r.hasAttachedBathroom,
        }));
      }
      const created = await api.post("/api/properties", payload);
      // The property is already saved at this point — a photo failing to
      // upload shouldn't strand the form in an error state or risk a
      // duplicate property on retry, so this loop fails silently per-photo.
      for (const dataUrl of photos) {
        try {
          await api.post(`/api/properties/${created.property.id}/photos`, { dataUrl });
        } catch {
          // property is saved either way; photos can be added later from the listing
        }
      }
      if (category !== "pg" && !multiUnit && hasExistingTenant && tenantName.trim()) {
        try {
          await api.post(`/api/properties/${created.property.id}/tenant`, {
            name: tenantName,
            phone: tenantPhone,
            aadhaar: tenantAadhaar,
            aadhaarPhotoFront: tenantAadhaarPhotoFront,
            aadhaarPhotoBack: tenantAadhaarPhotoBack,
            startDate: tenantStartDate,
            rentDueDay: Math.min(31, Math.max(1, Number(tenantRentDueDay) || 5)),
            noticePeriodDays: Math.min(365, Math.max(0, Number(tenantNoticePeriodDays) || 60)),
          });
        } catch {
          // property is saved either way; the tenant can be added later from the listing
        }
      }
      if (category !== "pg" && multiUnit) {
        // created.property.units come back in the same order they were
        // submitted in, so pairing by index lines each draft back up with
        // the real unit id the server just generated for it.
        for (let i = 0; i < filteredUnits.length; i++) {
          const draft = filteredUnits[i];
          const createdUnit = created.property.units?.[i];
          if (!draft.hasExistingTenant || !draft.tenantName.trim() || !createdUnit) continue;
          try {
            await api.post(`/api/units/${createdUnit.id}/tenant`, {
              name: draft.tenantName,
              phone: draft.tenantPhone,
              aadhaar: draft.tenantAadhaar,
              aadhaarPhotoFront: draft.tenantAadhaarPhotoFront,
              aadhaarPhotoBack: draft.tenantAadhaarPhotoBack,
              startDate: draft.tenantStartDate,
              rentDueDay: Math.min(31, Math.max(1, Number(draft.tenantRentDueDay) || 5)),
              noticePeriodDays: Math.min(365, Math.max(0, Number(draft.tenantNoticePeriodDays) || 60)),
            });
          } catch {
            // property is saved either way; the tenant can be added later from the listing
          }
        }
      }
      onDone();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t("common.somethingWrong"));
      setErrorIsPlanLimit(err instanceof ApiError && err.status === 402);
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 14 }}>
        <button type="button" className="icon-btn" onClick={goBack} aria-label="Back">‹</button>
        <div style={{ flex: 1, height: 5, borderRadius: 99, background: "var(--brand-100)", overflow: "hidden" }}>
          <div style={{ height: "100%", width: `${progress}%`, background: "var(--brand-700)", borderRadius: 99, transition: "width .3s ease" }} />
        </div>
      </div>
      {error && (
        <div className="error-banner">
          {error}
          {errorIsPlanLimit && (
            <button type="button" className="btn-solid btn-sm" style={{ marginTop: 10 }} onClick={() => onNavigate?.("plans")}>
              {t("addPropertyForm.upgradeNowBtn")}
            </button>
          )}
        </div>
      )}

      {step === "basics" && (
        <>
          <div className="chip-row">
            <div
              className={`chip${category === "independent" ? " selected" : ""}`}
              onClick={() => { setCategory("independent"); setType("2BHK"); }}
            >
              {t("addPropertyForm.chipIndependent")}
            </div>
            <div className={`chip${category === "pg" ? " selected" : ""}`} onClick={() => setCategory("pg")}>
              {t("addPropertyForm.chipPgHostel")}
            </div>
            <div
              className={`chip${category === "commercial" ? " selected" : ""}`}
              onClick={() => { setCategory("commercial"); setType("Shop"); }}
            >
              {t("addPropertyForm.chipCommercial")}
            </div>
          </div>

          <div className="field">
            <label>{t("addPropertyForm.propertyNameLabel")}<span className="required-mark">*</span></label>
            <div className="input-wrap"><input value={label} onChange={(e) => setLabel(e.target.value)} placeholder={t("addPropertyForm.propertyNamePlaceholder")} /></div>
          </div>

          {category !== "pg" && (
            <>
              <button
                type="button"
                className={`btn-highlight${multiUnit ? " selected" : ""}`}
                onClick={() => setMultiUnit((v) => !v)}
              >
                {t("addPropertyForm.multiUnitChip")}
              </button>
              <div className="field-hint" style={{ margin: "6px 0 14px" }}>{t("addPropertyForm.multiUnitHint")}</div>
            </>
          )}

          <button className="btn-solid" onClick={continueBasics}>{t("common.continue")}</button>
        </>
      )}

      {step === "location" && (
        <>
          <LocationCaptureField
            mapsKey={mapsKey}
            location={location}
            onChange={setLocation}
            onLocalityDetected={(text, force) => setLocality((prev) => (force || !prev.trim() ? text : prev))}
            onAddressDetected={(parts, force) => {
              setCity((prev) => (force || !prev.trim() ? parts.city : prev));
              setState((prev) => (force || !prev.trim() ? parts.state : prev));
              setPincode((prev) => (force || !prev.trim() ? parts.pincode : prev));
            }}
          />
          <div className="field">
            <label>{t("addPropertyForm.localityLabel")}<span className="required-mark">*</span></label>
            <div className="input-wrap"><input value={locality} onChange={(e) => setLocality(e.target.value)} placeholder={t("addPropertyForm.localityPlaceholder")} /></div>
          </div>
          <div style={{ display: "flex", gap: 10 }}>
            <div className="field" style={{ flex: 1 }}>
              <label>{t("addPropertyForm.cityLabel")}<span className="required-mark">*</span></label>
              <div className="input-wrap"><input value={city} onChange={(e) => setCity(e.target.value)} placeholder={t("addPropertyForm.cityPlaceholder")} /></div>
            </div>
            <div className="field" style={{ flex: 1 }}>
              <label>{t("addPropertyForm.stateLabel")}<span className="required-mark">*</span></label>
              <Select
                value={state}
                onChange={setState}
                placeholder={t("addPropertyForm.statePlaceholder")}
                options={INDIAN_STATES.map((s) => ({ value: s, label: s }))}
              />
            </div>
          </div>
          <div className="field">
            <label>{t("addPropertyForm.pincodeLabel")}<span className="required-mark">*</span></label>
            <div className="input-wrap">
              <input
                value={pincode}
                onChange={(e) => setPincode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                placeholder={t("addPropertyForm.pincodePlaceholder")}
                inputMode="numeric"
              />
            </div>
          </div>
          <button className="btn-solid" onClick={continueLocation}>{t("common.continue")}</button>
        </>
      )}

      {step === "typePricing" && (
        <>
          <div className="field">
            <label>{t("addPropertyForm.typeLabel")}<span className="required-mark">*</span></label>
            <Select
              value={type}
              onChange={setType}
              options={(category === "commercial" ? COMMERCIAL_TYPES : RESIDENTIAL_TYPES).map((pt) => ({ value: pt, label: pt }))}
            />
          </div>
          <div style={{ display: "flex", gap: 10 }}>
            <div className="field" style={{ flex: 1 }}>
              <label>{t("addPropertyForm.expectedRentLabel")}<span className="required-mark">*</span></label>
              <div className="input-wrap"><input type="number" value={expectedRent} onChange={(e) => setExpectedRent(e.target.value)} /></div>
            </div>
            <div className="field" style={{ flex: 1 }}>
              <label>{t("addPropertyForm.expectedDepositLabel")}<span className="required-mark">*</span></label>
              <div className="input-wrap"><input type="number" value={expectedDeposit} onChange={(e) => setExpectedDeposit(e.target.value)} /></div>
            </div>
          </div>
          <div className="field">
            <label>{t("addPropertyForm.maintenanceLabel")}</label>
            <div className="input-wrap"><input type="number" value={maintenance} onChange={(e) => setMaintenance(e.target.value)} placeholder={t("addPropertyForm.maintenancePlaceholder")} /></div>
          </div>
          <button className="btn-solid" onClick={continueTypePricing}>{t("common.continue")}</button>
        </>
      )}

      {step === "details" && (
        <>
          <div className="field">
            <label>{t("editPropertyForm.furnishingStatusLabel")}<span className="required-mark">*</span></label>
            <div className="chip-row">
              {FURNISHING_STATUSES.map((f) => (
                <div
                  key={f}
                  className={`chip${furnishingStatus === f ? " selected" : ""}`}
                  onClick={() => setFurnishingStatus(furnishingStatus === f ? null : f)}
                >
                  {t(`editPropertyForm.commercialFurnishing_${f}`)}
                </div>
              ))}
            </div>
          </div>
          <div className="field">
            <label>{t("editPropertyForm.floorNumberLabel")}</label>
            <div className="input-wrap">
              <input
                value={floorNumber}
                onChange={(e) => setFloorNumber(e.target.value)}
                placeholder={t("editPropertyForm.floorNumberPlaceholder")}
              />
            </div>
          </div>
          <div className="field">
            <label>{t("editPropertyForm.furnishingItemsLabel")}</label>
            <div className="field-hint" style={{ marginBottom: 8 }}>{t("editPropertyForm.furnishingItemsHint")}</div>
            <FurnishingItemsEditor
              presetItems={furnishingItems}
              onPresetChange={(key, value) => setFurnishingItems((prev) => ({ ...prev, [key]: value }))}
              removedPresets={removedPresets}
              onRemovedPresetsChange={setRemovedPresets}
              customItems={customItems}
              onCustomItemsChange={setCustomItems}
            />
          </div>
          <>
              <div className="section-title" style={{ margin: "14px 0 6px" }}>
                {t("addPropertyForm.commercialDetailsHeading")}
              </div>
              <div style={{ display: "flex", gap: 10 }}>
                <div className="field" style={{ flex: 1 }}>
                  <label>{t("addPropertyForm.carpetAreaLabel")}</label>
                  <div className="input-wrap"><input type="number" value={carpetAreaSqft} onChange={(e) => setCarpetAreaSqft(e.target.value)} /></div>
                </div>
                <div className="field" style={{ flex: 1 }}>
                  <label>{t("addPropertyForm.builtUpAreaLabel")}</label>
                  <div className="input-wrap"><input type="number" value={builtUpAreaSqft} onChange={(e) => setBuiltUpAreaSqft(e.target.value)} /></div>
                </div>
              </div>

              <div style={{ display: "flex", gap: 10 }}>
                <div className="field" style={{ flex: 1 }}>
                  <label>{t("addPropertyForm.lockInMonthsLabel")}</label>
                  <div className="input-wrap"><input type="number" value={lockInMonths} onChange={(e) => setLockInMonths(e.target.value)} /></div>
                </div>
                <div className="field" style={{ flex: 1 }}>
                  <label>{t("addPropertyForm.rentEscalationPercentLabel")}</label>
                  <div className="input-wrap"><input type="number" value={rentEscalationPercent} onChange={(e) => setRentEscalationPercent(e.target.value)} placeholder="5" /></div>
                </div>
                <div className="field" style={{ flex: 1 }}>
                  <label>{t("addPropertyForm.rentEscalationYearsLabel")}</label>
                  <div className="input-wrap"><input type="number" value={rentEscalationYears} onChange={(e) => setRentEscalationYears(e.target.value)} placeholder="2" /></div>
                </div>
              </div>

              <div className="field">
                <label>{t("addPropertyForm.possessionStatusLabel")}</label>
                <div className="chip-row">
                  {["ready", "under_fitout"].map((s) => (
                    <div
                      key={s}
                      className={`chip${possessionStatus === s ? " selected" : ""}`}
                      onClick={() => setPossessionStatus(possessionStatus === s ? null : s)}
                    >
                      {t(`addPropertyForm.possession_${s}`)}
                    </div>
                  ))}
                </div>
              </div>

              <div className="field">
                <label>{t("addPropertyForm.washroomTypeLabel")}</label>
                <div className="chip-row">
                  {["attached", "shared"].map((w) => (
                    <div
                      key={w}
                      className={`chip${washroomType === w ? " selected" : ""}`}
                      onClick={() => setWashroomType(washroomType === w ? null : w)}
                    >
                      {t(`addPropertyForm.washroom_${w}`)}
                    </div>
                  ))}
                </div>
              </div>

              <div className="field">
                <label>{t("addPropertyForm.commercialAmenitiesLabel")}</label>
                <div className="chip-row">
                  <div className={`chip${powerBackup ? " selected" : ""}`} onClick={() => setPowerBackup(powerBackup ? null : true)}>
                    {t("addPropertyForm.powerBackupChip")}
                  </div>
                  <div
                    className={`chip${gstRegistrationPossible ? " selected" : ""}`}
                    onClick={() => setGstRegistrationPossible(gstRegistrationPossible ? null : true)}
                  >
                    {t("addPropertyForm.gstChip")}
                  </div>
                  <div className={`chip${frontageSignage ? " selected" : ""}`} onClick={() => setFrontageSignage(frontageSignage ? null : true)}>
                    {t("addPropertyForm.frontageChip")}
                  </div>
                </div>
              </div>

              <div style={{ display: "flex", gap: 10 }}>
                <div className="field" style={{ flex: 2 }}>
                  <label>{t("addPropertyForm.powerLoadDetailsLabel")}</label>
                  <div className="input-wrap">
                    <input
                      value={powerLoadDetails}
                      onChange={(e) => setPowerLoadDetails(e.target.value)}
                      placeholder={t("addPropertyForm.powerLoadDetailsPlaceholder")}
                    />
                  </div>
                </div>
                <div className="field" style={{ flex: 1 }}>
                  <label>{t("addPropertyForm.parkingSpotsLabel")}</label>
                  <div className="input-wrap"><input type="number" value={parkingSpots} onChange={(e) => setParkingSpots(e.target.value)} /></div>
                </div>
              </div>

              {(type === "Office" || type === "Co-working") && (
                <div style={{ display: "flex", gap: 10 }}>
                  <div className="field" style={{ flex: 1 }}>
                    <label>{t("addPropertyForm.seatsCountLabel")}</label>
                    <div className="input-wrap"><input type="number" value={seatsCount} onChange={(e) => setSeatsCount(e.target.value)} /></div>
                  </div>
                  <div className="field" style={{ flex: 1 }}>
                    <label>{t("addPropertyForm.cabinsCountLabel")}</label>
                    <div className="input-wrap"><input type="number" value={cabinsCount} onChange={(e) => setCabinsCount(e.target.value)} /></div>
                  </div>
                  <div className="field" style={{ flex: 1 }}>
                    <label>{t("addPropertyForm.meetingRoomsCountLabel")}</label>
                    <div className="input-wrap"><input type="number" value={meetingRoomsCount} onChange={(e) => setMeetingRoomsCount(e.target.value)} /></div>
                  </div>
                </div>
              )}
            </>
          <button className="btn-solid" onClick={continueDetails}>{t("common.continue")}</button>
        </>
      )}

      {step === "houses" && (
        <>
              <div className="section-title" style={{ margin: "14px 0 6px" }}>
                {t("addPropertyForm.housesHeading")}
              </div>
              {units.map((u, i) => (
                <div key={i} className="card" style={{ background: "var(--surface-alt)" }}>
                  <div className="card-row" style={{ marginBottom: 8 }}>
                    <div className="field" style={{ flex: 1, marginBottom: 0 }}>
                      <label>{t("addPropertyForm.houseNameLabel")}</label>
                      <div className="input-wrap"><input value={u.label} onChange={(e) => updateUnit(i, { label: e.target.value })} /></div>
                    </div>
                    {units.length > 1 && (
                      <button type="button" className="link-btn" style={{ color: "var(--danger)", flexShrink: 0 }} onClick={() => setUnits((us) => us.filter((_, idx) => idx !== i))}>
                        {t("common.remove")}
                      </button>
                    )}
                  </div>
                  <div className="field" style={{ marginBottom: 8 }}>
                    <label>{t("addPropertyForm.typeLabel")}</label>
                    <Select
                      value={u.type}
                      onChange={(v) => updateUnit(i, { type: v })}
                      options={(category === "commercial" ? COMMERCIAL_TYPES : RESIDENTIAL_TYPES).map((pt) => ({ value: pt, label: pt }))}
                    />
                  </div>
                  <div style={{ display: "flex", gap: 8 }}>
                    <div className="field" style={{ flex: 1, marginBottom: 0 }}>
                      <label>{t("addPropertyForm.expectedRentLabel")}</label>
                      <div className="input-wrap"><input type="number" value={u.expectedRent} onChange={(e) => updateUnit(i, { expectedRent: e.target.value })} /></div>
                    </div>
                    <div className="field" style={{ flex: 1, marginBottom: 0 }}>
                      <label>{t("addPropertyForm.expectedDepositLabel")}</label>
                      <div className="input-wrap"><input type="number" value={u.expectedDeposit} onChange={(e) => updateUnit(i, { expectedDeposit: e.target.value })} /></div>
                    </div>
                  </div>
                  <div className="field" style={{ marginBottom: 8, marginTop: 8 }}>
                    <label>{t("addPropertyForm.maintenanceLabel")}</label>
                    <div className="input-wrap"><input type="number" value={u.maintenance} onChange={(e) => updateUnit(i, { maintenance: e.target.value })} placeholder={t("addPropertyForm.maintenancePlaceholder")} /></div>
                  </div>

                  <div className="field" style={{ marginBottom: 8 }}>
                    <label>{t("editPropertyForm.furnishingStatusLabel")}</label>
                    <div className="chip-row">
                      {FURNISHING_STATUSES.map((f) => (
                        <div
                          key={f}
                          className={`chip${u.furnishingStatus === f ? " selected" : ""}`}
                          onClick={() => updateUnit(i, { furnishingStatus: u.furnishingStatus === f ? null : f })}
                        >
                          {t(`editPropertyForm.${category === "commercial" ? "commercialFurnishing" : "furnishing"}_${f}`)}
                        </div>
                      ))}
                    </div>
                  </div>

                  {category !== "commercial" && (
                    <div className="field" style={{ marginBottom: 8 }}>
                      <label>{t("editPropertyForm.communityTypeLabel")}</label>
                      <div className="chip-row">
                        {COMMUNITY_TYPES.map((c) => (
                          <div
                            key={c}
                            className={`chip${u.communityType === c ? " selected" : ""}`}
                            onClick={() => updateUnit(i, { communityType: u.communityType === c ? null : c })}
                          >
                            {t(`editPropertyForm.community_${c}`)}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {(category === "commercial" || FLOOR_COMMUNITY_TYPES.includes(u.communityType ?? "")) && (
                    <div className="field" style={{ marginBottom: 8 }}>
                      <label>{t("editPropertyForm.floorNumberLabel")}</label>
                      <div className="input-wrap">
                        <input
                          value={u.floorNumber}
                          onChange={(e) => updateUnit(i, { floorNumber: e.target.value })}
                          placeholder={t("editPropertyForm.floorNumberPlaceholder")}
                        />
                      </div>
                    </div>
                  )}

                  <div className="field" style={{ marginBottom: 8 }}>
                    <label>{t("editPropertyForm.furnishingItemsLabel")}</label>
                    <div className="field-hint" style={{ marginBottom: 8 }}>{t("editPropertyForm.furnishingItemsHint")}</div>
                    <FurnishingItemsEditor
                      presetItems={u.furnishingItems}
                      onPresetChange={(key, value) => updateUnit(i, { furnishingItems: { ...u.furnishingItems, [key]: value } })}
                      removedPresets={u.removedPresetItems}
                      onRemovedPresetsChange={(keys) => updateUnit(i, { removedPresetItems: keys })}
                      customItems={u.customItems}
                      onCustomItemsChange={(items) => updateUnit(i, { customItems: items })}
                    />
                  </div>

                  <div className="chip-row" style={{ marginBottom: u.hasExistingTenant ? 12 : 0 }}>
                    <div className={`chip${!u.hasExistingTenant ? " selected" : ""}`} onClick={() => updateUnit(i, { hasExistingTenant: false })}>
                      {t("ownerListings.vacantBadge")}
                    </div>
                    <div className={`chip highlight${u.hasExistingTenant ? " selected" : ""}`} onClick={() => updateUnit(i, { hasExistingTenant: true })}>
                      {t("addPropertyForm.hasExistingTenantChip")}
                    </div>
                  </div>
                  {u.hasExistingTenant && (
                    <div className="card" style={{ background: "var(--surface)", marginBottom: 0 }}>
                      <div className="field">
                        <div className="field-label-row">
                          <label>{t("addPropertyForm.tenantNameLabel")}</label>
                          <ContactPickerButton onPick={(c) => updateUnit(i, { tenantName: c.name, tenantPhone: c.phone })} />
                        </div>
                        <div className="input-wrap"><input value={u.tenantName} onChange={(e) => updateUnit(i, { tenantName: e.target.value })} /></div>
                      </div>
                      <div className="field">
                        <label>{t("addPropertyForm.phoneLabel")}</label>
                        <div className="input-wrap"><input value={u.tenantPhone} onChange={(e) => updateUnit(i, { tenantPhone: e.target.value })} placeholder={t("addPropertyForm.phonePlaceholder")} /></div>
                      </div>
                      <div className="field">
                        <label>{t("addPropertyForm.aadhaarLabel")}</label>
                        <div className="input-wrap"><input value={u.tenantAadhaar} onChange={(e) => updateUnit(i, { tenantAadhaar: e.target.value })} /></div>
                      </div>
                      <AadhaarPhotoFields
                        front={u.tenantAadhaarPhotoFront}
                        back={u.tenantAadhaarPhotoBack}
                        onChangeFront={(dataUrl) => updateUnit(i, { tenantAadhaarPhotoFront: dataUrl })}
                        onChangeBack={(dataUrl) => updateUnit(i, { tenantAadhaarPhotoBack: dataUrl })}
                      />
                      <div className="field">
                        <label>{t("addPropertyForm.dateOfOccupancyLabel")}</label>
                        <div className="input-wrap"><input type="date" value={u.tenantStartDate} onChange={(e) => updateUnit(i, { tenantStartDate: e.target.value })} /></div>
                        <div className="field-hint">{t("addPropertyForm.dateHint")}</div>
                      </div>
                      <div className="field">
                        <label>{t("assignTenantForm.rentDueDayLabel")}</label>
                        <div className="input-wrap">
                          <input type="number" min={1} max={31} value={u.tenantRentDueDay} onChange={(e) => updateUnit(i, { tenantRentDueDay: e.target.value })} />
                        </div>
                        <div className="field-hint">{t("assignTenantForm.rentDueDayHint")}</div>
                      </div>
                      <div className="field" style={{ marginBottom: 0 }}>
                        <label>{t("assignTenantForm.noticePeriodLabel")}</label>
                        <div className="input-wrap">
                          <input type="number" min={0} max={365} value={u.tenantNoticePeriodDays} onChange={(e) => updateUnit(i, { tenantNoticePeriodDays: e.target.value })} />
                        </div>
                        <div className="field-hint">{t("assignTenantForm.noticePeriodHint")}</div>
                      </div>
                    </div>
                  )}
                </div>
              ))}
              <button
                type="button"
                className="btn-outline btn-sm"
                style={{ width: "100%", marginBottom: 14 }}
                onClick={() => setUnits((us) => [...us, blankUnit(`House ${us.length + 1}`)])}
              >
                {t("addPropertyForm.addHouseLink")}
              </button>
          <button className="btn-solid" onClick={goNext}>{t("common.continue")}</button>
        </>
      )}

      {step === "pgDetails" && (
        <>
          <div className="field">
            <label>{t("addPropertyForm.occupancyLabel")}</label>
            <Select
              value={occupancyType}
              onChange={(v) => setOccupancyType(v as any)}
              options={[
                { value: "boys", label: t("addPropertyForm.occBoys") },
                { value: "girls", label: t("addPropertyForm.occGirls") },
                { value: "co-ed", label: t("addPropertyForm.occCoEd") },
              ]}
            />
          </div>
          <div className="field">
            <label>{t("addPropertyForm.mealPlanLabel")}</label>
            <div className="chip-row">
              {MEAL_OPTIONS.map((m) => (
                <div
                  key={m}
                  className={`chip${mealPlan.includes(m) ? " selected" : ""}`}
                  onClick={() => toggleInList(mealPlan, setMealPlan, m)}
                >
                  {t(`addPropertyForm.meal_${m}`)}
                </div>
              ))}
            </div>
          </div>
          <div className="field">
            <label>{t("addPropertyForm.preferredTenantsLabel")}</label>
            <div className="chip-row">
              {TENANT_TYPE_OPTIONS.map((tt) => (
                <div
                  key={tt}
                  className={`chip${preferredTenants.includes(tt) ? " selected" : ""}`}
                  onClick={() => toggleInList(preferredTenants, setPreferredTenants, tt)}
                >
                  {t(`addPropertyForm.tenantType_${tt}`)}
                </div>
              ))}
            </div>
          </div>
          <button className="btn-solid" onClick={goNext}>{t("common.continue")}</button>
        </>
      )}

      {step === "rooms" && (
        <>
          <div className="section-title" style={{ margin: "14px 0 6px" }}>
            {t("addPropertyForm.roomsHeading")}
            <small
              onClick={() =>
                setRooms((rs) => [
                  ...rs,
                  { sharingType: "Double Sharing", rentPerBed: "", depositPerBed: "", bedCount: "2", hasAc: false, hasAttachedBathroom: false },
                ])
              }
            >
              {t("addPropertyForm.addRoomLink")}
            </small>
          </div>
          {rooms.map((r, i) => (
            <div key={i} className="card" style={{ background: "var(--surface-alt)" }}>
              <div className="field" style={{ marginBottom: 8 }}>
                <label>{t("addPropertyForm.sharingTypeLabel")}</label>
                <Select
                  value={r.sharingType}
                  onChange={(v) => updateRoom(i, { sharingType: v })}
                  options={["Single", "Double Sharing", "Triple Sharing", "Four Sharing"].map((s) => ({ value: s, label: s }))}
                />
              </div>
              <div style={{ display: "flex", gap: 8 }}>
                <div className="field" style={{ flex: 1, marginBottom: 0 }}>
                  <label>{t("addPropertyForm.rentPerBedLabel")}</label>
                  <div className="input-wrap"><input type="number" value={r.rentPerBed} onChange={(e) => updateRoom(i, { rentPerBed: e.target.value })} /></div>
                </div>
                <div className="field" style={{ flex: 1, marginBottom: 0 }}>
                  <label>{t("addPropertyForm.depositPerBedLabel")}</label>
                  <div className="input-wrap"><input type="number" value={r.depositPerBed} onChange={(e) => updateRoom(i, { depositPerBed: e.target.value })} /></div>
                </div>
                <div className="field" style={{ flex: 1, marginBottom: 0 }}>
                  <label>{t("addPropertyForm.bedsLabel")}</label>
                  <div className="input-wrap"><input type="number" value={r.bedCount} onChange={(e) => updateRoom(i, { bedCount: e.target.value })} /></div>
                </div>
              </div>
              <div className="chip-row" style={{ marginTop: 8 }}>
                <div className={`chip${r.hasAc ? " selected" : ""}`} onClick={() => updateRoom(i, { hasAc: !r.hasAc })}>
                  {t("addPropertyForm.roomAc")}
                </div>
                <div
                  className={`chip${r.hasAttachedBathroom ? " selected" : ""}`}
                  onClick={() => updateRoom(i, { hasAttachedBathroom: !r.hasAttachedBathroom })}
                >
                  {t("addPropertyForm.roomAttachedBathroom")}
                </div>
              </div>
            </div>
          ))}
          <button className="btn-solid" onClick={goNext}>{t("common.continue")}</button>
        </>
      )}

      {step === "furnishing" && (
        <>
          <div className="field">
            <label>{t("editPropertyForm.furnishingStatusLabel")}<span className="required-mark">*</span></label>
            <div className="chip-row">
              {FURNISHING_STATUSES.map((f) => (
                <div
                  key={f}
                  className={`chip${furnishingStatus === f ? " selected" : ""}`}
                  onClick={() => setFurnishingStatus(furnishingStatus === f ? null : f)}
                >
                  {t(`editPropertyForm.furnishing_${f}`)}
                </div>
              ))}
            </div>
          </div>

          <div className="field">
              <label>{t("editPropertyForm.communityTypeLabel")}<span className="required-mark">*</span></label>
              <div className="chip-row">
                {COMMUNITY_TYPES.map((c) => (
                  <div
                    key={c}
                    className={`chip${communityType === c ? " selected" : ""}`}
                    onClick={() => setCommunityType(communityType === c ? null : c)}
                  >
                    {t(`editPropertyForm.community_${c}`)}
                  </div>
                ))}
              </div>
            </div>

          {FLOOR_COMMUNITY_TYPES.includes(communityType ?? "") && (
            <div className="field">
              <label>{t("editPropertyForm.floorNumberLabel")}</label>
              <div className="input-wrap">
                <input
                  value={floorNumber}
                  onChange={(e) => setFloorNumber(e.target.value)}
                  placeholder={t("editPropertyForm.floorNumberPlaceholder")}
                />
              </div>
            </div>
          )}

          <div className="field">
            <label>{t("editPropertyForm.furnishingItemsLabel")}</label>
            <div className="field-hint" style={{ marginBottom: 8 }}>{t("editPropertyForm.furnishingItemsHint")}</div>
            <FurnishingItemsEditor
              presetItems={furnishingItems}
              onPresetChange={(key, value) => setFurnishingItems((prev) => ({ ...prev, [key]: value }))}
              removedPresets={removedPresets}
              onRemovedPresetsChange={setRemovedPresets}
              customItems={customItems}
              onCustomItemsChange={setCustomItems}
            />
          </div>

          <div className="field">
            <label>{t("addPropertyForm.amenitiesLabel")}</label>
            <div className="chip-row" style={{ flexWrap: "wrap" }}>
              {AMENITY_PRESETS.map((a) => (
                <div key={a} className={`chip${amenities.includes(a) ? " selected" : ""}`} onClick={() => toggleAmenity(a)}>
                  {t(`addPropertyForm.amenity_${a}`)}
                </div>
              ))}
              {amenities.filter((a) => !AMENITY_PRESETS.includes(a)).map((a) => (
                <div key={a} className="chip selected" onClick={() => toggleAmenity(a)}>
                  {a}
                </div>
              ))}
            </div>
            <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
              <div className="input-wrap" style={{ flex: 1, marginBottom: 0 }}>
                <input
                  value={customAmenityInput}
                  onChange={(e) => setCustomAmenityInput(e.target.value)}
                  placeholder={t("addPropertyForm.addAmenityPlaceholder")}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      addCustomAmenity();
                    }
                  }}
                />
              </div>
              <button type="button" className="btn-outline btn-sm" onClick={addCustomAmenity}>
                {t("addPropertyForm.addAmenityBtn")}
              </button>
            </div>
          </div>

          <button className="btn-solid" style={{ marginTop: 12 }} onClick={continueFurnishing}>{t("common.continue")}</button>
        </>
      )}

      {step === "tenant" && (
        <>
        <button
          type="button"
          className={`btn-highlight${hasExistingTenant ? " selected" : ""}`}
          onClick={() => setHasExistingTenant((v) => !v)}
        >
          {t("addPropertyForm.hasExistingTenantChip")}
        </button>
      {hasExistingTenant && (
        <div className="card" style={{ background: "var(--surface-alt)", marginTop: 12, marginBottom: 0 }}>
          <div className="field">
            <div className="field-label-row">
              <label>{t("addPropertyForm.tenantNameLabel")}</label>
              <ContactPickerButton onPick={(c) => { setTenantName(c.name); setTenantPhone(c.phone); }} />
            </div>
            <div className="input-wrap"><input value={tenantName} onChange={(e) => setTenantName(e.target.value)} /></div>
          </div>
          <div className="field">
            <label>{t("addPropertyForm.phoneLabel")}</label>
            <div className="input-wrap"><input value={tenantPhone} onChange={(e) => setTenantPhone(e.target.value)} placeholder={t("addPropertyForm.phonePlaceholder")} /></div>
          </div>
          <div className="field">
            <label>{t("addPropertyForm.aadhaarLabel")}</label>
            <div className="input-wrap"><input value={tenantAadhaar} onChange={(e) => setTenantAadhaar(e.target.value)} /></div>
          </div>
          <AadhaarPhotoFields
            front={tenantAadhaarPhotoFront}
            back={tenantAadhaarPhotoBack}
            onChangeFront={setTenantAadhaarPhotoFront}
            onChangeBack={setTenantAadhaarPhotoBack}
          />
          <div className="field">
            <label>{t("addPropertyForm.dateOfOccupancyLabel")}</label>
            <div className="input-wrap"><input type="date" value={tenantStartDate} onChange={(e) => setTenantStartDate(e.target.value)} /></div>
            <div className="field-hint">{t("addPropertyForm.dateHint")}</div>
          </div>
          <div className="field">
            <label>{t("assignTenantForm.rentDueDayLabel")}</label>
            <div className="input-wrap">
              <input type="number" min={1} max={31} value={tenantRentDueDay} onChange={(e) => setTenantRentDueDay(e.target.value)} />
            </div>
            <div className="field-hint">{t("assignTenantForm.rentDueDayHint")}</div>
          </div>
          <div className="field" style={{ marginBottom: 0 }}>
            <label>{t("assignTenantForm.noticePeriodLabel")}</label>
            <div className="input-wrap">
              <input type="number" min={0} max={365} value={tenantNoticePeriodDays} onChange={(e) => setTenantNoticePeriodDays(e.target.value)} />
            </div>
            <div className="field-hint">{t("assignTenantForm.noticePeriodHint")}</div>
          </div>
        </div>
      )}
          <button className="btn-solid" style={{ marginTop: 12 }} onClick={goNext}>{t("common.continue")}</button>
        </>
      )}

      {step === "photos" && (
        <>
          <PhotoStager photos={photos} onChange={setPhotos} />
          <div className="field-hint">{t("addPropertyForm.photosSkipHint")}</div>
          <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
            <button className="btn-outline" onClick={onCancel}>
              {t("common.cancel")}
            </button>
            <button className="btn-solid" disabled={busy} onClick={submit}>
              {busy ? t("common.saving") : t("addPropertyForm.addPropertyButton")}
            </button>
          </div>
          {photos.length === 0 && (
            <button className="link-btn" style={{ marginTop: 8 }} disabled={busy} onClick={submit}>
              {t("addPropertyForm.addPropertyLaterButton")}
            </button>
          )}
        </>
      )}
    </>
  );
}
